/**********************************************************************
 * DYNAMIC SUBMISSION FORM (Section-by-Section Validation + Skip Wizard)
 *
 * This is the main form component for creating and editing asset submissions.
 * It supports:
 * - Dynamic form generation from database configurations (form ID 7)
 * - Hardcoded form configuration for form ID 3 (Inventory & Tagging)
 * - Multi-step wizard with validation
 * - File uploads with OCR processing
 * - Auto-save drafts functionality
 * - Field skipping capabilities
 * - Plugin system for enhanced field processing
 * - Custom fields (uniqueFields) stored in organization_forms table
 * - Conditional field rendering based on user input
 * - Special restructuring for form ID 3 (5-page workflow)
 *
 * Key Features:
 * - Responsive design with mobile support
 * - Real-time validation and error highlighting
 * - Integration with Core API and legacy Alfred service
 * - Conditional field rendering for custom fields (e.g., roomNumber, officeNumber)
 * - Computed fields with dynamic calculations
 * - Image preview and management
 * - Location-based functionality
 * - Organization logo from user store
 *
 * Form ID 3 (Inventory & Tagging):
 * - Hardcoded base configuration (same for all organizations)
 * - Logo dynamically added from organization config
 * - Custom fields (uniqueFields) appended from organization_forms.config
 * - Custom fields appear in "Additional Information" section before Summary
 * - Custom fields support conditional logic (e.g., show based on assetLocation)
 * - Custom fields section is hidden if no fields are visible
 *
 * Form ID 7 and others:
 * - Form configuration fetched from database via Core API
 * - Supports legacy form structures
 *
 * Structure:
 * - Form ID 3: Hardcoded config + logo + custom fields
 * - Form ID 7+: Form configuration loaded from database via Core API
 * - Sections rendered as steps in a wizard
 * - Fields rendered dynamically based on type
 * - State management using useReducer
 * - Auto-save using debounced API calls
 *
 * API Integration:
 * - Core API endpoints for equipment areas, asset types, manufacturers
 * - Core API endpoints for location data
 * - Legacy Alfred service for backward compatibility
 * - Endpoint response caching for performance
 **********************************************************************/

// Icons used throughout the form
import {
	InboxOutlined, // File upload drag area icon
	InfoCircleOutlined, // Tooltip information icon
	LoadingOutlined, // Loading spinner icon
} from "@ant-design/icons";

// Ant Design components for form UI
import {
	Button, // Action buttons (Next, Previous, Submit, etc.)
	Checkbox, // Skip field checkboxes
	Col, // Grid column layout
	DatePicker, // Date input fields
	Form, // Main form wrapper
	type FormInstance, // TypeScript type for form instance
	Image, // Image preview component
	Input, // Text input fields
	InputNumber, // Number input fields
	Modal, // Image preview modal
	Progress, // Progress bar for multi-step forms
	Radio, // Radio button fields
	Row, // Grid row layout
	Select, // Dropdown select fields
	Spin, // Loading spinner wrapper
	Steps, // Multi-step navigation component
	Tag, // Tags for dynamic list items
	Tooltip, // Tooltip wrapper for help text
	Typography, // Text styling components
	Upload, // File upload component
	message, // Toast notifications
	theme, // Ant Design theme tokens
} from "antd";

// External libraries
import axios from "axios"; // HTTP client for API calls
import dayjs from "dayjs"; // Date manipulation library

// React core imports
import React, {
	FC, // Functional Component type
	Fragment, // React Fragment for grouping elements
	type ReactNode, // Type for React elements
	useCallback, // Hook for memoizing functions
	useEffect, // Hook for side effects
	useMemo, // Hook for memoizing values
	useReducer, // Hook for complex state management
	useRef, // Hook for DOM references
	useState, // Hook for component state
} from "react";

// React Router for navigation
import { useLocation, useNavigate, useParams } from "react-router-dom";

// Internal component imports
import { DynamicLocationPicker } from "@/components/inputs/dynamic-location-input"; // Custom location picker component
import { RatingInput } from "@/components/inputs/rating-input"; // Custom rating input component
import { Loader } from "@/layouts/signed-in/Loader/Loader"; // Loading spinner component

// Service imports
import { auth } from "@/services/auth-service"; // Firebase authentication service
import {
	decodeManufacturerDate,
	hasManufacturerDecoder,
} from "@/services/manufacturer-date-decoder.service";
import { uploadFieldCaptureFromDataUrl } from "@/services/storage-service"; // File upload service
import {
	createDraft, // Create new draft submission
	submitSubmission, // Submit final submission
	updateDraft, // Update existing draft
	updateSubmission, // Update existing submission
} from "@/services/submission-service";
import { isHeicFile, processImageFile } from "@/utils/heic-converter"; // HEIC to JPEG converter

// Store imports for state management
import { useFormStore, useUserStore } from "@/stores"; // Zustand stores for form and user state

// Utility imports
import { debounce } from "@/utils/debounce"; // Debouncing function for auto-save
import type { UploadFile } from "antd/es/upload/interface"; // TypeScript type for file uploads
import { LuXCircle } from "react-icons/lu"; // Close icon for image preview

// Environment variables
import { VITE_ALFRED_SERVICE_URL } from "@/constants/env"; // API base URL

/*-----------------------------------------------------------------------
 * DYNAMIC STYLES - Loading Field Indicators
 *
 * These styles provide visual feedback when fields are being processed
 * by OCR or other plugins. They create a disabled/loading appearance
 * to indicate that the field is being automatically populated.
 *-----------------------------------------------------------------------*/

const loadingFieldStyles = `
/* Main loading field styles - applied to various input types */
.field-loading .ant-input,
.field-loading .ant-select-selector,
.field-loading .ant-picker,
.field-loading .ant-input-number,
.field-loading .ant-radio-group,
.field-loading .ant-checkbox-wrapper,
.loading-field.ant-input,
.loading-field.ant-select .ant-select-selector,
.loading-field.ant-picker,
.loading-field.ant-input-number {
	background-color: #f5f5f5 !important;  /* Light gray background */
	color: #bfbfbf !important;             /* Muted text color */
	opacity: 0.7 !important;               /* Slightly transparent */
	cursor: not-allowed !important;        /* Indicate non-interactive */
	transition: all 0.3s ease !important;  /* Smooth transition */
	border-color: #d9d9d9 !important;     /* Muted border color */
}

/* Icons and suffixes in loading fields */
.field-loading .ant-select-arrow,
.field-loading .ant-picker-suffix,
.loading-field.ant-select .ant-select-arrow,
.loading-field.ant-picker .ant-picker-suffix {
	color: #bfbfbf !important;             /* Muted icon color */
}

/* Form labels for loading fields */
.field-loading .ant-form-item-label > label {
	color: #8c8c8c !important;             /* Muted label color */
}

/* Overlay effect for loading fields */
.field-loading::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(255, 255, 255, 0.5);  /* Semi-transparent overlay */
	pointer-events: none;                   /* Don't block interactions */
	z-index: 1;                            /* Appear above field */
	border-radius: 6px;                    /* Match field border radius */
}
`;

// Inject styles into document head (only once per page load)
if (
	typeof document !== "undefined" &&
	!document.getElementById("field-loading-styles")
) {
	const styleElement = document.createElement("style");
	styleElement.id = "field-loading-styles";
	styleElement.textContent = loadingFieldStyles;
	document.head.appendChild(styleElement);
}
/*-----------------------------------------------------------------------
 * PLUGIN SYSTEM IMPORTS
 *
 * The plugin system allows for enhanced field processing, particularly
 * for OCR, barcode scanning, and other automated data extraction.
 *-----------------------------------------------------------------------*/
import {
	type DFSPluginFn, // TypeScript type for plugin functions
	dfsPluginRegistry, // Registry of available plugins
} from "@/services/dynamic-form-system-plugin.service";

import { CORE_URL } from "@/constants/api";
// Form configuration service
import { VITE_CORE_API_URL } from "@/constants/env";
import { getFormData } from "@/services/form-service";

/**
 * Hardcoded form configuration for Form ID 3 (Inventory & Tagging)
 * This is the core structure that is the same for every organization.
 * Logo and custom fields (uniqueFields) will be added dynamically from organization config.
 */
const FORM_ID_3_BASE_CONFIG: DFSFormConfig = {
	config: {
		formName: "Inventory & Tagging",
		sections: [
			{
				title: "General Information",
				fields: [
					{
						name: "location_id",
						type: "location",
						label: "Store Location",
						validation: { required: true },
					},
				],
			},
			{
				title: "Asset Details",
				fields: [
					{
						name: "equipmentPhoto",
						type: "file",
						label: "Equipment Photo",
						metadata: {
							propMap: { assetCondition: "conditionRating" },
							pluginName: "assetCondition",
						},
						validation: { required: true },
						draggerProps: {
							accept: "image/*",
							multiple: false,
						},
					},
					{
						name: "manufacturerPlatePhoto",
						type: "file",
						label: "Manufacturer Plate Photo",
						metadata: {
							propMap: {
								make_id: "manufacturer",
								model_number: "modelNumber",
								serial_number: "serialNumber",
								refrigerant_type: "refrigerant",
								manufacturer_date: "manufacturerDate",
								power_supply_amps: "amps",
								power_supply_voltage: "voltage",
								refrigerant_quantity: "refrigerantCharge",
								power_supply_frequency: "frequency",
								refrigerant_quantity_unit: "refrigerantUnit",
							},
							photoType: "MANUFACTURER_PLATE",
							pluginName: "ocrImage",
						},
						validation: { required: true },
						draggerProps: {
							accept: "image/*",
							multiple: false,
						},
					},
					{
						name: "assetLocation",
						type: "select",
						label: "Asset Location",
						dataSource: {
							type: "api",
							endpoint: `${VITE_CORE_API_URL}${CORE_URL}/organization/equipment-areas`,
							metadata: {
								label: "label",
								value: "value",
								responsePath: "data",
							},
						},
						validation: { required: true },
						placeholder: "Select Asset Location",
					},
					{
						name: "assetType",
						type: "select",
						label: "Asset Type",
						dataSource: {
							type: "api",
							endpoint: `${VITE_CORE_API_URL}${CORE_URL}/organization/asset-types`,
							metadata: {
								label: "name",
								value: "id",
								responsePath: "data.assetTypes",
							},
						},
						validation: { required: true },
						placeholder: "Select Asset Type",
					},
					{
						name: "manufacturer",
						type: "select",
						label: "Manufacturer",
						dataSource: {
							type: "api",
							endpoint: `${VITE_CORE_API_URL}${CORE_URL}/utility/manufacturers`,
							metadata: {
								label: "name",
								value: "id",
								responsePath: "data.manufacturers",
							},
						},
						validation: { required: true },
						placeholder: "Select Manufacturer",
					},
					{
						name: "modelNumber",
						type: "text",
						label: "Model Number",
						validation: { required: true },
						placeholder: "Enter Model Number",
					},
					{
						name: "serialNumber",
						type: "text",
						label: "Serial Number",
						validation: { required: true },
						placeholder: "Enter Serial Number",
					},
					{
						name: "manufacturerDate",
						type: "date",
						label: "Manufacturer Date",
						validation: { required: true },
						placeholder: "Select Manufacturer Date",
					},
					{
						name: "assetTagPhoto",
						type: "file",
						label: "Asset Tag Photo",
						metadata: {
							propMap: { decoded_value: "assetTag" },
							photoType: "ID_TAG",
							pluginName: "ocrImage",
						},
						validation: { required: true },
						draggerProps: {
							accept: "image/*",
							multiple: false,
						},
					},
					{
						name: "assetTag",
						type: "text",
						label: "Asset Tag",
						validation: { required: true },
						placeholder: "Enter Asset Tag",
					},
				],
			},
			{
				title: "Asset Condition & Summary",
				fields: [
					{
						name: "conditionRating",
						type: "rating",
						label: "Condition Rating",
						validation: { required: true },
					},
					{
						name: "assetSummary",
						type: "textarea",
						label: "Asset Summary",
						validation: { required: true },
						placeholder: "Enter Asset Summary",
					},
				],
			},
		],
	},
};

/**
 * Helper function to get Form ID 3 configuration with logo
 * Custom fields are appended separately after restructuring, so they're not included here
 */
function getFormId3Config(orgLogo?: string): DFSFormConfig {
	return {
		...FORM_ID_3_BASE_CONFIG,
		config: {
			...FORM_ID_3_BASE_CONFIG.config,
			logo: orgLogo || "",
		},
	};
}

/*-----------------------------------------------------------------------
 * COMPONENT EXTRACTIONS
 *
 * Extract specific components from Ant Design libraries for cleaner code
 *-----------------------------------------------------------------------*/
const { Title } = Typography; // Typography title component
const { Dragger } = Upload; // File upload dragger component
const { Step } = Steps; // Individual step component for navigation

/*-----------------------------------------------------------------------
 * TYPESCRIPT INTERFACES & TYPES
 *
 * These interfaces define the structure of the dynamic form system,
 * including field definitions, validation rules, and state management.
 *-----------------------------------------------------------------------*/

/**
 * Plugin Enhancement Interface
 *
 * Defines UI enhancements that plugins can apply to fields,
 * such as validation status, help text, and loading states.
 */
export interface PluginEnhancement {
	validateStatus?: "success" | "warning" | "error" | "validating"; // Visual validation state
	help?: string; // Help text to display
	suffix?: ReactNode; // Custom suffix element
	loading?: boolean; // Loading state indicator
}

/**
 * Plugin Enhancements State
 *
 * Maps field names to their respective plugin enhancements.
 */
export interface PluginEnhancementsState {
	[fieldName: string]: PluginEnhancement;
}

/**
 * Field Option Interface
 *
 * Defines structure for dropdown/select options.
 */
export interface DFSFieldOption {
	value: string | number; // The actual value stored
	label: string; // The display text
}

/**
 * Field Validation Interface
 *
 * Defines validation rules that can be applied to fields.
 */
export interface DFSFieldValidation {
	required?: boolean; // Whether field is required
	pattern?: RegExp; // Regex pattern for validation
	minLength?: number; // Minimum string length
	maxLength?: number; // Maximum string length
	min?: number; // Minimum numeric value
	max?: number; // Maximum numeric value
	message?: string; // Custom error message
}

/**
 * Field Data Source Interface
 *
 * Defines how field options are loaded (API, static, or function).
 */
export interface DFSFieldDataSource {
	type: "api" | "static" | "function"; // Source type
	endpoint?: string; // API endpoint URL
	options?: DFSFieldOption[]; // Static options array
	dependencies?: string[]; // Fields this depends on
	metadata?: Record<string, unknown>; // Additional metadata
	fetchFunction?: (
		values: Record<string, unknown>,
	) => Promise<DFSFieldOption[]>; // Custom fetch function
}

/**
 * Conditional Operators
 *
 * Supported operators for conditional field rendering.
 */
export type DFSConditionalOperator =
	| "equals" // Field equals specific value
	| "notEquals" // Field does not equal specific value
	| "in" // Field value is in array
	| "notIn" // Field value is not in array
	| "greaterThan" // Field value is greater than
	| "lessThan" // Field value is less than
	| "contains"; // Field array contains value

/**
 * Field Conditional Interface
 *
 * Defines conditions for showing/hiding fields.
 */
export interface DFSFieldConditional {
	dependsOn: string; // Field this condition depends on
	operator?: DFSConditionalOperator; // Comparison operator
	value?: unknown; // Value to compare against
	values?: unknown[]; // Array of values for in/notIn operators
}

/**
 * Field Format Interface
 *
 * Defines formatting options for field values.
 */
export interface DFSFieldFormat {
	formatFn?: string; // JavaScript function for formatting
	inputSuffix?: string; // Suffix to display in input
}

/**
 * Field Computed Interface
 *
 * Defines computed fields that calculate values from other fields.
 */
export interface DFSFieldComputed {
	dependsOn: string[]; // Fields this computation depends on
	calculate: ((...args: any[]) => number | string) | string; // Calculation function or expression
}

/**
 * Tooltip Link Interface
 *
 * Defines links that can appear in field tooltips.
 */
export interface DFSTooltipLink {
	url: string; // Link URL
	label: string; // Link display text
}

/**
 * Field Tooltip Interface
 *
 * Defines tooltip content for fields.
 */
export interface DFSFieldTooltip {
	text?: string; // Plain text tooltip
	html?: string; // HTML content tooltip
	links?: DFSTooltipLink[]; // Array of links
}

/**
 * Form Field Interface
 *
 * Main interface defining all possible field properties.
 */
export interface DFSFormField {
	type: string; // Field type (text, select, file, etc.)
	name?: string; // Field name for form handling
	key?: string; // Alternative field identifier
	responseTag?: string; // Legacy field identifier
	label?: string; // Display label
	tooltip?: DFSFieldTooltip; // Help tooltip
	placeholder?: string; // Input placeholder text
	required?: boolean; // Whether field is required
	disabled?: boolean; // Whether field is disabled/skipped
	hide?: boolean; // Whether field is hidden
	options?: DFSFieldOption[]; // Static options for select fields
	dataSource?: DFSFieldDataSource; // Dynamic data source
	conditional?: DFSFieldConditional; // Conditional rendering rules
	multiple?: boolean; // Allow multiple selections
	draggerProps?: {
		// File upload specific props
		multiple?: boolean; // Allow multiple files
		maxCount?: number; // Maximum file count
		accept?: string; // Accepted file types
	};
	fields?: DFSFormField[]; // Nested fields (for groups/lists)
	countField?: string; // Count field for dynamic lists
	validation?: DFSFieldValidation | DFSFieldValidation[]; // Validation rules
	note?: string; // Additional notes
	format?: DFSFieldFormat; // Formatting options
	computed?: DFSFieldComputed; // Computed field configuration
	value?: any; // Field value
	metadata?: Record<string, any>; // Additional metadata
}

/**
 * Form Section Interface
 *
 * Defines a section/step in the form.
 */
export interface DFSFormSection {
	title: string; // Section title
	description?: string; // Section description
	fields?: DFSFormField[]; // Array of fields in section
	questions?: DFSFormField[]; // Alternative field array name
	metadata?: Record<string, unknown>; // Additional metadata
}

/**
 * Form Configuration Interface
 *
 * Top-level form configuration structure.
 */
export interface DFSFormConfig {
	config: {
		sections: DFSFormSection[]; // Array of form sections
		metadata?: Record<string, unknown>; // Form metadata
		formVersion?: string; // Form version
		formName?: string; // Form display name
		logo?: string; // Form logo URL
	};
}

/**
 * Form State Interface
 *
 * Internal state management for the form component.
 */
export interface DFSFormState {
	currentStep: number; // Current step/section index
	loadingFields: Record<string, boolean>; // Loading state for each field
	fieldOptions: Record<string, DFSFieldOption[]>; // Loaded options for each field
	dynamicListState: Record<string, number>; // Count state for dynamic lists
	submitState: Record<string, any>; // Submission state
}

/**
 * Form Action Interface
 *
 * Redux-style action interface for state management.
 */
export interface DFSAction {
	type: string; // Action type
	payload?: any; // Action payload
}

/*-----------------------------------------------------------------------
 * STATE MANAGEMENT - REDUCER & INITIAL STATE
 *
 * This section defines the initial state and reducer for managing form state
 * using React's useReducer hook. The reducer handles various form actions
 * like navigation, loading states, and field options.
 *-----------------------------------------------------------------------*/

/**
 * Initial State
 *
 * Default state when the form component is first loaded.
 */
export const initialState: DFSFormState = {
	currentStep: 0, // Start at first step
	loadingFields: {}, // No fields loading initially
	fieldOptions: {}, // No options loaded initially
	dynamicListState: {}, // No dynamic lists initially
	submitState: {}, // No submission state initially
};

/**
 * Form Reducer
 *
 * Handles state updates for the form component. Uses Redux-style actions
 * to manage complex state transitions.
 *
 * @param state - Current form state
 * @param action - Action to perform
 * @returns New form state
 */
export function formReducer(
	state: DFSFormState,
	action: DFSAction,
): DFSFormState {
	switch (action.type) {
		// Update current step/section
		case "SET_CURRENT_STEP":
			return { ...state, currentStep: action.payload };

		// Update loading state for specific fields
		case "SET_LOADING_FIELD":
			return {
				...state,
				loadingFields: { ...state.loadingFields, ...action.payload },
			};

		// Update options for specific fields (from API calls)
		case "SET_FIELD_OPTIONS":
			return {
				...state,
				fieldOptions: { ...state.fieldOptions, ...action.payload },
			};

		// Update dynamic list counts
		case "SET_DYNAMIC_LIST_STATE":
			return {
				...state,
				dynamicListState: { ...state.dynamicListState, ...action.payload },
			};

		// Update submission state (loading, errors, etc.)
		case "SET_SUBMIT_STATE":
			return {
				...state,
				submitState: { ...state.submitState, ...action.payload },
			};

		// Reset form state but preserve field options
		case "RESET_FORM_STATE":
			return {
				...initialState,
				fieldOptions: state.fieldOptions, // Preserve loaded options
			};

		// Return current state for unknown actions
		default:
			return state;
	}
}

/*-----------------------------------------------------------------------
 * UTILITY FUNCTIONS
 *
 * Core utility functions for form processing, validation, and data manipulation.
 * These functions handle dynamic lists, tooltips, field names, and other
 * common operations throughout the form system.
 *-----------------------------------------------------------------------*/

/**
 * Get Dynamic List Count
 *
 * Retrieves the count of items in a dynamic list field from the form values.
 * Dynamic lists allow users to add/remove multiple instances of the same field group.
 *
 * @param allValues - All form values
 * @param field - The dynamic list field configuration
 * @returns Number of items in the dynamic list
 */
function getDynamicListCount(
	allValues: Record<string, any>,
	field: DFSFormField,
): number {
	if (!field.countField || !field.name) return 0;

	let countFieldPath = field.countField;

	// Handle nested field paths - if parent's name has a dot, we need to prefix countField
	if (!field.countField.includes(".") && field.name.includes(".")) {
		const pathSegments = field.name.split(".");
		pathSegments.pop(); // Remove the last segment (field name)
		const parentPath = pathSegments.join(".");
		countFieldPath = parentPath
			? `${parentPath}.${field.countField}`
			: field.countField;
	}

	const val = allValues[countFieldPath];
	if (val === undefined || val === null) return 0;

	const num = Number.parseInt(val, 10);
	return Number.isNaN(num) ? 0 : num;
}

/** Return a tooltip if present */
function renderTooltipContent(tooltip: DFSFieldTooltip): ReactNode {
	const { text, html, links } = tooltip;
	return (
		<div style={{ maxWidth: "300px" }}>
			{html && (
				<div
					dangerouslySetInnerHTML={{ __html: html }}
					style={{ marginBottom: text || links ? "8px" : 0 }}
				/>
			)}
			{!html && text && (
				<div style={{ marginBottom: links ? "8px" : 0 }}>{text}</div>
			)}
			{links && links.length > 0 && (
				<ul style={{ paddingLeft: "18px", margin: 0 }}>
					{links.map((link, idx) => (
						<li key={`${link.url}-${idx}`}>
							<a
								style={{ color: "white" }}
								href={link.url}
								target="_blank"
								rel="noopener noreferrer"
							>
								{link.label}
							</a>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

/** Build label + tooltip if needed */
function getFieldLabel(field: DFSFormField): ReactNode {
	if (!field.label && !field.tooltip) return field.label || null;
	if (!field.tooltip) return field.label;
	return (
		<span>
			{field.label}
			<Tooltip title={renderTooltipContent(field.tooltip)} placement="top">
				<InfoCircleOutlined style={{ marginLeft: 8, cursor: "pointer" }} />
			</Tooltip>
		</span>
	);
}

/** Recursively gather all field names for a set of fields */
function getFieldNames(fields?: DFSFormField[]): string[] {
	if (!fields) return [];
	let names: string[] = [];
	for (const f of fields) {
		if (f.type === "group" || f.type === "dynamicList") {
			if (f.fields) {
				names = names.concat(getFieldNames(f.fields));
			}
		} else {
			const fieldName = f.name || f.responseTag || f.key;
			if (fieldName) names.push(fieldName);
		}
	}
	return names;
}

/** Recursively gather all fields in all sections */
function getAllFields(sections: DFSFormSection[]): DFSFormField[] {
	const all: DFSFormField[] = [];
	sections.forEach((sec) => {
		const fs = sec.fields || sec.questions || [];
		all.push(...getAllFieldsFromArray(fs));
	});
	return all;
}
function getAllFieldsFromArray(fields: DFSFormField[]): DFSFormField[] {
	let all: DFSFormField[] = [];
	for (const f of fields) {
		all.push(f);
		if (f.fields && f.fields.length > 0) {
			all.push(...getAllFieldsFromArray(f.fields));
		}
	}
	return all;
}

/** If the user wrote a computed field as string, convert to a real JS function. */
function prepareComputedFields(formConfig: DFSFormConfig) {
	const allFields = getAllFields(formConfig.config.sections);
	allFields.forEach((field) => {
		if (field.computed && typeof field.computed.calculate === "string") {
			try {
				const rawExpr = (field.computed.calculate as string).trim();
				const safeExpr = sanitizeComputeExpression(
					rawExpr,
					field.computed.dependsOn,
				);
				const varDeclarations = (field.computed.dependsOn || [])
					.map((dep) => {
						return `
							const ${dep} = (function() {
								const val = values?.${dep};
								if (typeof val === 'number') return val;
								if (val === null || val === undefined) return 0;
								const num = Number(val);
								return isNaN(num) ? 0 : num;
							})();
						`;
					})
					.join("\n");

				const fnBody = `
					try {
						${varDeclarations}
						const result = ${safeExpr};
						return result;
					} catch (error) {
						console.error('Error in computed calc:', error);
						return 0;
					}
				`;

				const computeFn = new Function("values", fnBody);
				// Test invocation
				computeFn({});
				field.computed.calculate = computeFn as (
					...args: any[]
				) => number | string;
			} catch (error) {
				console.error(`Error preparing computed field ${field.name}:`, error);
				field.computed.calculate = () => 0;
			}
		}
	});
}

/** Compute all fields that have a .computed => update form values accordingly */
function computeAllFields(
	allValues: Record<string, any>,
	formConfig: DFSFormConfig | null,
): Record<string, any> {
	if (!formConfig) return allValues;

	const allFields = getAllFields(formConfig.config.sections);
	const computedFields = allFields.filter((f) => f.computed);
	if (computedFields.length === 0) return allValues;

	let hasChanges = false;
	const newValues = { ...allValues };
	for (const field of computedFields) {
		if (!field.name || !field.computed?.calculate) continue;
		try {
			const calc = field.computed.calculate;
			if (typeof calc !== "function") continue;
			const newValue = (calc as Function)(newValues);
			const numericValue = typeof newValue === "number" ? newValue : 0;
			if (numericValue !== newValues[field.name]) {
				newValues[field.name] = numericValue;
				hasChanges = true;
			}
		} catch (err) {
			console.error(`Error computing ${field.name}:`, err);
			if (newValues[field.name] !== 0) {
				newValues[field.name] = 0;
				hasChanges = true;
			}
		}
	}
	return hasChanges ? newValues : allValues;
}

/** Strip out "values." references and validate dependencies. */
function sanitizeComputeExpression(
	expr: string,
	dependsOn: string[] = [],
): string {
	let cleaned = expr.replace(/values\?\./g, "").replace(/values\./g, "");
	const usedVars = cleaned.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
	const unknownVars = usedVars.filter(
		(v) =>
			!dependsOn.includes(v) &&
			!["true", "false", "null", "undefined"].includes(v),
	);
	if (unknownVars.length > 0) {
		throw new Error(`Expression uses unknown vars: ${unknownVars.join(", ")}`);
	}
	return cleaned;
}

/** Build initial values from a fresh, empty form config (just default field values). */
function buildInitialValuesFromFormConfig(config: DFSFormConfig) {
	const initialValues: Record<string, any> = {};

	function processFields(fields: DFSFormField[], prefix = "") {
		fields.forEach((f) => {
			const fieldName = f.name || f.responseTag || f.key;
			if (!fieldName) return;
			const fullName = prefix ? `${prefix}.${fieldName}` : fieldName;

			if (f.value !== undefined) {
				if (
					f.type === "date" &&
					f.value &&
					(typeof f.value === "string" ||
						typeof f.value === "number" ||
						f.value instanceof Date)
				) {
					initialValues[fullName] = dayjs(f.value);
				} else if (f.type === "image" || f.type === "file") {
					initialValues[fullName] = Array.isArray(f.value)
						? f.value.map((url: string, idx: number) => ({
								uid: `-${idx}`,
								name: `file-${idx}`,
								status: "done",
								url,
								response: { url },
								thumbUrl: url,
							}))
						: [];
				} else {
					initialValues[fullName] = f.value;
				}
			}

			if (f.type === "group" && f.fields) {
				processFields(f.fields, fullName);
			}
			if (f.type === "dynamicList" && f.fields && f.countField) {
				const defaultCount = Array.isArray(f.value) ? f.value.length : 0;
				initialValues[f.countField] = defaultCount;
			}
		});
	}

	config.config.sections.forEach((sec) => {
		const arr = sec.fields || sec.questions || [];
		processFields(arr);
	});

	return initialValues;
}

/** Build initial values from a parseInputs object. */
function buildInitialValuesFromParsedInputs(parsedInputs: any) {
	const initialValues: Record<string, any> = {};
	const dynamicCounts: Record<string, number> = {};

	function processFields(fields: DFSFormField[], prefix = "") {
		fields.forEach((f) => {
			const fieldName = f.responseTag || f.name || f.key;
			if (!fieldName) return;
			const fullName = prefix ? `${prefix}.${fieldName}` : fieldName;

			if (f.type === "image" || f.type === "file") {
				if (Array.isArray(f.value)) {
					initialValues[fullName] = f.value.map((url: string, idx: number) => ({
						uid: `-${idx}`,
						name: `file-${idx}`,
						status: "done",
						url,
						response: { url },
						thumbUrl: url,
					}));
				} else {
					initialValues[fullName] = [];
				}
				return;
			}

			if (
				f.type === "date" &&
				f.value &&
				(typeof f.value === "string" ||
					typeof f.value === "number" ||
					f.value instanceof Date)
			) {
				initialValues[fullName] = dayjs(f.value);
				return;
			}

			if (f.type === "group" && f.fields && Array.isArray(f.value)) {
				// process group
				f.fields.forEach((subField) => {
					const sfName = subField.name || subField.key || subField.responseTag;
					if (!sfName) return;
					const subFull = `${fullName}.${sfName}`;
					const subVal = extractGroupValue(
						f.value as any[],
						sfName,
						subField.type,
					);
					initialValues[subFull] = subVal;
				});
				return;
			}

			if (f.type === "dynamicList" && Array.isArray(f.value)) {
				if (f.countField) {
					const cntPath = prefix ? `${prefix}.${f.countField}` : f.countField;
					initialValues[cntPath] = f.value.length;
					dynamicCounts[fullName] = f.value.length;
				}
				f.value.forEach((item: any, i: number) => {
					if (!f.fields) return;
					f.fields.forEach((sf) => {
						const sfName = sf.name || sf.key || sf.responseTag;
						if (!sfName) return;
						const sfFull = `${fullName}.${i}.${sfName}`;
						let val = item[sfName];
						if (sf.type === "image" || sf.type === "file") {
							if (Array.isArray(val)) {
								val = val.map((u: string, idx: number) => ({
									uid: `-${idx}`,
									name: `file-${idx}`,
									status: "done",
									url: u,
									response: { url: u },
									thumbUrl: u,
								}));
							} else {
								val = [];
							}
						} else if (
							sf.type === "date" &&
							val &&
							(typeof val === "string" ||
								typeof val === "number" ||
								val instanceof Date)
						) {
							val = dayjs(val);
						}
						initialValues[sfFull] = val ?? "";
					});
				});
				return;
			}

			if (f.fields && f.fields.length > 0) {
				processFields(f.fields, fullName);
				return;
			}

			initialValues[fullName] = f.value;
		});
	}

	const sections = parsedInputs.sections || [];
	sections.forEach((sec: any) => {
		const arr = sec.fields || sec.questions || [];
		processFields(arr);
	});

	return { initialValues, dynamicCounts };
}

function extractGroupValue(
	groupValueArr: any[],
	subFieldName: string,
	subFieldType: string,
) {
	// Some older structures come as arrays of name/value pairs
	const oldStyle = groupValueArr[0] && Array.isArray(groupValueArr[0]);
	if (oldStyle) {
		const firstArr = groupValueArr[0];
		const found = firstArr.find((el: any) => el?.name === subFieldName);
		if (found) {
			let val = found.value;
			if (subFieldType === "image" || subFieldType === "file") {
				if (Array.isArray(val)) {
					val = val.map((url: string, idx: number) => ({
						uid: `-${idx}`,
						name: `file-${idx}`,
						status: "done",
						url,
						response: { url },
						thumbUrl: url,
					}));
				} else {
					val = [];
				}
			} else if (subFieldType === "date" && val) {
				val = dayjs(new Date(val));
			}
			return val;
		}
		return null;
	}
	// Possibly new style => array of objects {name, value}
	const found = groupValueArr.find((v: any) => v.name === subFieldName);
	if (found) {
		let val = found.value;
		if (subFieldType === "image" || subFieldType === "file") {
			if (Array.isArray(val)) {
				val = val.map((url: string, idx: number) => ({
					uid: `-${idx}`,
					name: `file-${idx}`,
					status: "done",
					url,
					response: { url },
					thumbUrl: url,
				}));
			} else {
				val = [];
			}
		} else if (subFieldType === "date" && val) {
			val = dayjs(new Date(val));
		}
		return val;
	}
	return null;
}

/** If a field has parentField logic, filter the data accordingly. */
function getFilteredOptions(
	field: DFSFormField,
	data: any,
	values: Record<string, any>,
) {
	if (!field.dataSource?.metadata?.parentField) return data;

	const metadata = field.dataSource.metadata as any;
	const { parentField, filterKey = "id", optionsPath = "value" } = metadata;

	const parentValue = values[parentField as string];
	if (!parentValue) return [];

	if (!Array.isArray(data)) {
		console.warn("data is not an array:", data);
		return [];
	}

	const matchingRecords = data.filter(
		(record: any) => record.label && record.label[filterKey] === parentValue,
	);
	const allOptions = matchingRecords.reduce(
		(acc: any[], record: any) => {
			if (record.label && Array.isArray(record.label[optionsPath])) {
				acc.push(...record.label[optionsPath]);
			}
			return acc;
		},
		[] as Array<{ name: string; id: number }>,
	);

	return allOptions.map((opt: any) => ({
		label: opt.name,
		value: opt.id,
	}));
}

/** Convert the flattened antd formData => original JSON shape (like your DB format). */
function buildSubmissionData(
	formConfig: DFSFormConfig | null,
	formData: Record<string, any>,
): { title: string; sections: any[] } | null {
	if (!formConfig) return null;

	// Create a copy of formData to include unique fields that may be conditionally hidden
	const enhancedFormData = { ...formData };

	// Find all unique fields in the form configuration and preserve their values
	// even if they're conditionally hidden
	const allFields = getAllFields(formConfig.config.sections);
	allFields.forEach((field) => {
		if (field.metadata?.uniqueField === true && field.name) {
			// If the unique field has a value in the current form state, preserve it
			// This ensures unique fields like archivedAssetTag are always included
			if (formData[field.name] !== undefined) {
				enhancedFormData[field.name] = formData[field.name];
			}
		}
	});

	function processValue(field: DFSFormField, val: any): any {
		if (field.type === "image" || field.type === "file") {
			if (!val || !Array.isArray(val) || val.length === 0) return [];
			return val.map((f: any) => f.url || f.response?.url);
		}
		if (field.type === "date" && val && dayjs.isDayjs(val)) {
			return val.toISOString();
		}
		if (field.type === "location" && val && typeof val === "object") {
			return val.id || val;
		}
		return val;
	}

	function reconstructDynamicList(
		field: DFSFormField,
		fd: Record<string, any>,
		prefix: string,
	): any[] {
		if (!field.fields || !field.countField) return [];
		const cntPath = field.name || field.responseTag || field.key || "";
		const fullCountPath = prefix ? `${prefix}.${cntPath}` : cntPath;
		const cnt = getDynamicListCount(fd, { ...field, name: fullCountPath });
		const items: any[] = [];
		for (let i = 0; i < cnt; i++) {
			const itemObj: Record<string, any> = {};
			field.fields.forEach((subField) => {
				const sfName = subField.name || subField.responseTag || subField.key;
				if (!sfName) return;
				const fullPath = prefix
					? `${prefix}.${cntPath}.${i}.${sfName}`
					: `${cntPath}.${i}.${sfName}`;
				let val = fd[fullPath];
				if (subField.type === "dynamicList") {
					val = reconstructDynamicList(
						subField,
						fd,
						`${prefix ? prefix + "." : ""}${cntPath}.${i}`,
					);
				} else if (subField.fields && subField.fields.length > 0) {
					val = processSectionFields(
						subField.fields,
						fd,
						`${prefix ? prefix + "." : ""}${cntPath}.${i}`,
					);
				} else {
					val = processValue(subField, val);
				}
				itemObj[sfName] = val;
			});
			items.push(itemObj);
		}
		return items;
	}

	function processSectionFields(
		fields: DFSFormField[],
		fd: Record<string, any>,
		prefix = "",
	): any[] {
		const results: any[] = [];
		fields.forEach((field) => {
			const fieldName = field.name || field.responseTag || field.key;
			if (!fieldName) return;
			const fullName = prefix ? `${prefix}.${fieldName}` : fieldName;

			let val = fd[fullName];
			if (field.type === "dynamicList") {
				val = reconstructDynamicList(field, fd, prefix);
			} else if (field.type === "group" && field.fields) {
				const groupArr: any[] = [];
				field.fields.forEach((sf) => {
					const sfName = sf.name || sf.responseTag || sf.key;
					if (!sfName) return;
					const subFull = `${fullName}.${sfName}`;
					let subVal = fd[subFull];
					subVal = processValue(sf, subVal);
					groupArr.push({ ...sf, name: sfName, value: subVal });
				});
				val = groupArr;
			} else if (field.fields && field.fields.length > 0) {
				val = processSectionFields(field.fields, fd, fullName);
			} else {
				val = processValue(field, val);
			}

			results.push({ ...field, value: val });
		});
		return results;
	}

	const sectionsOut: any[] = [];
	formConfig.config.sections.forEach((sec) => {
		const fArr = sec.fields || sec.questions || [];
		const secData = {
			title: sec.title,
			questions: processSectionFields(fArr, enhancedFormData),
		};
		sectionsOut.push(secData);
	});

	return {
		title: formConfig.config.sections?.[0]?.title || "",
		sections: sectionsOut,
	};
}

/** Ensure each section's fields is an array and do minor prep. */
function preprocessSections(formConfig: DFSFormConfig) {
	formConfig.config.sections.forEach((section) => {
		if (section.fields) {
			section.fields = preprocessFields(section.fields);
		} else if (section.questions) {
			section.questions = preprocessFields(section.questions);
		}
	});
}
function preprocessFields(fields: DFSFormField[]): DFSFormField[] {
	return fields.map((f) => {
		const copy = { ...f };
		if (copy.fields && copy.fields.length > 0) {
			copy.fields = preprocessFields(copy.fields);
		}
		return copy;
	});
}

/**
 * Convert custom fields from organization config to DFSFormField format
 * and append them as a new section before the last section (Summary & Additional Notes)
 */
function appendCustomFieldsToFormConfig(
	formConfig: DFSFormConfig,
	customFields: Array<{
		name: string;
		label: string;
		type: string;
		required?: boolean;
		placeholder?: string;
		options?: string[];
		conditional?: {
			dependsOn: string;
			operator?: string;
			value?: unknown;
			values?: unknown[];
		};
	}> | null,
): DFSFormConfig {
	if (!customFields || customFields.length === 0) {
		return formConfig;
	}

	// Convert custom fields to DFSFormField format
	const dfsFields: DFSFormField[] = customFields.map((field) => {
		const baseField: DFSFormField = {
			type: field.type === "select" ? "select" : field.type,
			name: field.name,
			label: field.label,
			required: field.required || false,
			placeholder: field.placeholder,
			validation: field.required ? { required: true } : {},
			metadata: {
				uniqueField: true, // Mark as unique field for identification
			},
		};

		// Add options for select fields
		if (field.type === "select" && field.options && field.options.length > 0) {
			baseField.options = field.options.map((opt) => ({
				label: opt,
				value: opt,
			}));
		}

		// Preserve conditional logic if present
		if (field.conditional) {
			baseField.conditional = {
				dependsOn: field.conditional.dependsOn,
				operator: field.conditional.operator as
					| DFSConditionalOperator
					| undefined,
				value: field.conditional.value,
				values: field.conditional.values as unknown[] | undefined,
			};
		}

		return baseField;
	});

	// Create a new section for custom fields
	const customFieldsSection: DFSFormSection = {
		title: "Additional Information",
		fields: dfsFields,
	};

	// Insert the custom fields section before the last section (Summary & Additional Notes)
	const sections = [...formConfig.config.sections];
	const lastSectionIndex = sections.length - 1;

	const updatedSections = [
		...sections.slice(0, lastSectionIndex),
		customFieldsSection,
		...sections.slice(lastSectionIndex),
	];

	// Build updated config with custom fields section inserted before the summary section
	const updatedConfig: DFSFormConfig = {
		...formConfig,
		config: {
			...formConfig.config,
			sections: updatedSections,
		},
	};

	return updatedConfig;
}

/** Helper: is the value effectively empty for a required field? */
function isValueEmpty(value: any, fieldType?: string): boolean {
	if (value === null || value === undefined) return true;
	if (value === "") return true;
	if (Array.isArray(value)) {
		if ((fieldType === "image" || fieldType === "file") && value.length === 0) {
			return true;
		}
	}
	if (fieldType === "date" && !value) return true;
	return false;
}

/**
 * Restructure Form Configuration
 *
 * Transforms the original database form configuration into a structured 5-page workflow
 * specifically designed for asset inventory and tagging. This function is only applied
 * to form ID 3 and creates a consistent user experience regardless of the original
 * form structure.
 *
 * The 5-page structure:
 * 1. Store Location (required)
 * 2. Asset Photo + OCR populated fields
 * 3. Manufacturer Plate information (conditional)
 * 4. Asset Tag information (conditional)
 * 5. Summary & Additional Notes
 *
 * @param originalConfig - The original form configuration from database
 * @returns Restructured form configuration with 5-page workflow
 */
function restructureFormConfig(originalConfig: DFSFormConfig): DFSFormConfig {
	// Extract all fields from original config for reuse
	const allOriginalFields = getAllFields(originalConfig.config.sections);

	// Helper function to find a field by exact name match
	const findField = (name: string) =>
		allOriginalFields.find((f) => f.name === name);

	// Helper function to find fields by pattern (currently unused but may be useful)
	const findFieldsByPattern = (pattern: RegExp) =>
		allOriginalFields.filter((f) => f.name && pattern.test(f.name));

	// PAGE 1: Location Field
	// Use existing location field or create default if not found
	const locationField = findField("location_id") || {
		name: "location_id",
		type: "location",
		label: "Store Location",
		validation: { required: true },
		placeholder: "Select Store Location",
	};

	// PAGE 2: Core Asset Fields (preserve original configuration)
	const equipmentPhotoField = findField("equipmentPhoto");
	const assetLocationField = findField("assetLocation");
	const roomNumberField = findField("roomNumber");
	const officeNumberField = findField("officeNumber");
	const assetTypeField = findField("assetType");
	const manufacturerField = findField("manufacturer");
	const conditionRatingField = findField("conditionRating");
	const qsrStoreField = findField("qsrStore"); // Quick service restaurant store field

	// Organization-specific fields (preserve original configuration)
	const storeNameField = findField("storeName");
	const storeTypeField = findField("storeType");

	// PAGE 3: Manufacturer Plate Related Fields
	const manufacturerPlatePhotoField = findField("manufacturerPlatePhoto");
	const modelNumberField = findField("modelNumber");
	const serialNumberField = findField("serialNumber");
	const manufacturerDateField = findField("manufacturerDate");

	// Refrigerant related fields (for HVAC equipment)
	const refrigerantField = findField("refrigerant");
	const refrigerantChargeField = findField("refrigerantCharge");
	const refrigerantUnitField = findField("refrigerantUnit");

	// Check if any refrigerant fields exist in original config
	const hasRefrigerantFields = !!(
		refrigerantField ||
		refrigerantChargeField ||
		refrigerantUnitField
	);

	// Create default refrigerant fields if they don't exist
	const finalRefrigerantField =
		refrigerantField ||
		({
			name: "refrigerant",
			type: "select",
			label: "Refrigerant",
			options: [
				{ label: "R12", value: "R12" },
				{ label: "R1233ZD", value: "R1233ZD" },
				{ label: "R1234YF", value: "R1234YF" },
				{ label: "R1234ZE", value: "R1234ZE" },
				{ label: "R1336MZZ", value: "R1336MZZ" },
				{ label: "R134A", value: "R134A" },
				{ label: "R170", value: "R170" },
				{ label: "R22", value: "R22" },
				{ label: "R245FA", value: "R245FA" },
				{ label: "R290", value: "R290" },
				{ label: "R32", value: "R32" },
				{ label: "R401A", value: "R401A" },
				{ label: "R401B", value: "R401B" },
				{ label: "R401C", value: "R401C" },
				{ label: "R402A", value: "R402A" },
				{ label: "R404", value: "R404" },
				{ label: "R404A", value: "R404A" },
				{ label: "R407A", value: "R407A" },
				{ label: "R407C", value: "R407C" },
				{ label: "R408A", value: "R408A" },
				{ label: "R410A", value: "R410A" },
				{ label: "R413A", value: "R413A" },
				{ label: "R414A", value: "R414A" },
				{ label: "R418A", value: "R418A" },
				{ label: "R422D", value: "R422D" },
				{ label: "R424A", value: "R424A" },
				{ label: "R426A", value: "R426A" },
				{ label: "R428A", value: "R428A" },
				{ label: "R434A", value: "R434A" },
				{ label: "R438A", value: "R438A" },
				{ label: "R442A", value: "R442A" },
				{ label: "R448A", value: "R448A" },
				{ label: "R449A", value: "R449A" },
				{ label: "R450A", value: "R450A" },
				{ label: "R453A", value: "R453A" },
				{ label: "R454C", value: "R454C" },
				{ label: "R455A", value: "R455A" },
				{ label: "R464A", value: "R464A" },
				{ label: "R500", value: "R500" },
				{ label: "R502", value: "R502" },
				{ label: "R507", value: "R507" },
				{ label: "R513A", value: "R513A" },
				{ label: "R600A", value: "R600A" },
				{ label: "R717", value: "R717" },
				{ label: "R744", value: "R744" },
			],
			placeholder: "Select Refrigerant",
			validation: { required: false },
		} as DFSFormField);

	const finalRefrigerantChargeField =
		refrigerantChargeField ||
		({
			name: "refrigerantCharge",
			type: "number",
			label: "Refrigerant Quantity",
			placeholder: "Enter refrigerant amount",
			validation: { required: false },
		} as DFSFormField);

	const finalRefrigerantUnitField =
		refrigerantUnitField ||
		({
			name: "refrigerantUnit",
			type: "select",
			label: "Refrigerant Unit",
			options: [
				{ value: "lb", label: "lb" },
				{ value: "oz", label: "oz" },
			],
			placeholder: "Select Refrigerant Unit",
			validation: { required: false },
		} as DFSFormField);

	// Warranty fields (if they exist in the original config)
	const warrantyFields = allOriginalFields.filter(
		(f) => f.name && f.name.toLowerCase().includes("warranty"),
	);

	// PAGE 4: Asset Tag Fields
	const assetTagPhotoField = findField("assetTagPhoto");
	const assetTagField = findField("assetTag");
	const archivedAssetTagField = findField("archivedAssetTag");

	// PAGE 5: Summary and Notes
	const assetSummaryField = findField("assetSummary");

	// Build the restructured configuration
	const restructuredConfig: DFSFormConfig = {
		config: {
			sections: [
				// PAGE 1: Store Location (not skippable)
				{
					title: "Store Location",
					fields: [locationField],
				},

				// PAGE 2: Asset Photo + OCR populated fields
				{
					title: "Asset Photo",
					fields: [
						// Equipment photo field (with OCR processing)
						equipmentPhotoField ||
							({
								name: "equipmentPhoto",
								type: "file",
								label: "Take a Photo of the Equipment",
								metadata: {
									propMap: { assetCondition: "conditionRating" },
									pluginName: "assetCondition",
								},
								validation: { required: true },
								draggerProps: { accept: "image/*", multiple: false },
							} as DFSFormField),
						// Additional asset fields (only include if they exist)
						...(assetLocationField ? [assetLocationField] : []),
						...(roomNumberField ? [roomNumberField] : []),
						...(officeNumberField ? [officeNumberField] : []),
						...(assetTypeField ? [assetTypeField] : []),
						...(conditionRatingField ? [conditionRatingField] : []),
						...(qsrStoreField ? [qsrStoreField] : []),
						// Organization-specific fields (only include if they exist)
						...(storeNameField ? [storeNameField] : []),
						...(storeTypeField ? [storeTypeField] : []),
					].flat(),
				},

				// PAGE 3: Manufacturer Plate Information
				{
					title: "Manufacturer Plate",
					fields: [
						// Decision question: Is there a manufacturer plate?
						{
							name: "hasManufacturerPlate",
							type: "radio",
							label: "Is there a manufacturer plate visible?",
							options: [
								{ value: "yes", label: "Yes" },
								{ value: "no", label: "No" },
							],
							validation: { required: true },
						} as DFSFormField,

						// Conditional fields - only show if manufacturer plate exists
						...(manufacturerPlatePhotoField
							? [
									{
										...manufacturerPlatePhotoField,
										metadata: {
											...manufacturerPlatePhotoField.metadata,
											photoType: "MANUFACTURER_PLATE",
											propMap: {
												make: "manufacturer",
												model_number: "modelNumber",
												serial_number: "serialNumber",
												manufacture_date: "manufacturerDate",
												refrigerant_type: "refrigerant",
												refrigerant_quantity: "refrigerantCharge",
												refrigerant_quantity_unit: "refrigerantUnit",
											},
											pluginName: "ocrImage",
										},
										conditional: {
											dependsOn: "hasManufacturerPlate",
											operator: "equals" as DFSConditionalOperator,
											value: "yes",
										},
									} as DFSFormField,
								]
							: []),
						// Manufacturer field (conditional - only show if manufacturer plate exists)
						...(manufacturerField
							? [
									{
										...manufacturerField,
										conditional: {
											dependsOn: "hasManufacturerPlate",
											operator: "equals" as DFSConditionalOperator,
											value: "yes",
										},
									} as DFSFormField,
								]
							: []),
						...(modelNumberField
							? [
									{
										...modelNumberField,
										conditional: {
											dependsOn: "hasManufacturerPlate",
											operator: "equals" as DFSConditionalOperator,
											value: "yes",
										},
									} as DFSFormField,
								]
							: []),
						...(serialNumberField
							? [
									{
										...serialNumberField,
										conditional: {
											dependsOn: "hasManufacturerPlate",
											operator: "equals" as DFSConditionalOperator,
											value: "yes",
										},
									} as DFSFormField,
								]
							: []),
						...(manufacturerDateField
							? [
									{
										...manufacturerDateField,
										conditional: {
											dependsOn: "hasManufacturerPlate",
											operator: "equals" as DFSConditionalOperator,
											value: "yes",
										},
									} as DFSFormField,
								]
							: []),

						// Refrigeration Details question - only show if manufacturer plate exists
						{
							name: "hasRefrigerationDetails",
							type: "radio",
							label: "Are there refrigeration details on the plate?",
							options: [
								{ value: "yes", label: "Yes" },
								{ value: "no", label: "No" },
							],
							validation: { required: false },
							conditional: {
								dependsOn: "hasManufacturerPlate",
								operator: "equals" as DFSConditionalOperator,
								value: "yes",
							},
						} as DFSFormField,

						// Refrigerant fields - only show if refrigeration details are present
						{
							...finalRefrigerantField,
							conditional: {
								dependsOn: "hasRefrigerationDetails",
								operator: "equals" as DFSConditionalOperator,
								value: "yes",
							},
						} as DFSFormField,
						{
							...finalRefrigerantChargeField,
							conditional: {
								dependsOn: "hasRefrigerationDetails",
								operator: "equals" as DFSConditionalOperator,
								value: "yes",
							},
						} as DFSFormField,
						{
							...finalRefrigerantUnitField,
							conditional: {
								dependsOn: "hasRefrigerationDetails",
								operator: "equals" as DFSConditionalOperator,
								value: "yes",
							},
						} as DFSFormField,

						// Warranty fields (if they exist in original config)
						...warrantyFields.map(
							(field) =>
								({
									...field,
									conditional: {
										dependsOn: "hasManufacturerPlate",
										operator: "equals" as DFSConditionalOperator,
										value: "yes",
									},
								}) as DFSFormField,
						),
					],
				},

				// PAGE 4: Asset Tag Information
				{
					title: "Asset Tag",
					fields: [
						// Decision question: Is there an asset tag?
						{
							name: "hasAssetTag",
							type: "radio",
							label: "Is there an asset tag visible?",
							options: [
								{ value: "yes", label: "Yes" },
								{ value: "no", label: "No" },
							],
							validation: { required: true },
						} as DFSFormField,

						// Conditional fields - only show if asset tag exists
						...(assetTagPhotoField
							? [
									{
										...assetTagPhotoField,
										metadata: {
											...assetTagPhotoField.metadata,
											photoType: "ID_TAG",
											propMap: { decoded_value: "assetTag" },
											pluginName: "ocrImage",
										},
										conditional: {
											dependsOn: "hasAssetTag",
											operator: "equals" as DFSConditionalOperator,
											value: "yes",
										},
									} as DFSFormField,
								]
							: []),
						...(assetTagField
							? [
									{
										...assetTagField,
										conditional: {
											dependsOn: "hasAssetTag",
											operator: "equals" as DFSConditionalOperator,
											value: "yes",
										},
									} as DFSFormField,
								]
							: []),
						// Archived asset tag field (if it exists)
						...(archivedAssetTagField
							? [
									{
										...archivedAssetTagField,
										conditional: {
											dependsOn: "hasAssetTag",
											operator: "equals" as DFSConditionalOperator,
											value: "yes",
										},
									} as DFSFormField,
								]
							: []),
					],
				},

				// PAGE 5: Summary & Additional Notes
				{
					title: "Summary & Additional Notes",
					fields: [
						// Special summary display field (custom rendering)
						{
							name: "summaryDisplay",
							type: "text",
							label: "Review Information",
							disabled: true,
						},
						// Asset summary field (if it exists)
						...(assetSummaryField ? [assetSummaryField] : []),
						// Additional notes field (always present)
						{
							name: "additionalNotes",
							type: "textarea",
							label: "Additional Notes (Optional)",
							placeholder: "Enter any additional notes about this asset",
						} as DFSFormField,
					],
				},
			],
			// Preserve original metadata
			metadata: originalConfig.config.metadata,
			formVersion: originalConfig.config.formVersion,
			formName: originalConfig.config.formName,
			logo: originalConfig.config.logo,
		},
	};

	return restructuredConfig;
}

/*-----------------------------------------------------------------------
 * Main Component
 *-----------------------------------------------------------------------*/
export const DynamicSubmissionForm: FC = () => {
	const [form] = Form.useForm();
	const [formConfig, setFormConfig] = useState<DFSFormConfig | null>(null);
	const [formName, setFormName] = useState("");
	const [formLogo, setFormLogo] = useState("");
	const { token } = theme.useToken();

	const { user } = useUserStore();
	const { draftId, setDraftId, formData, setFormData } = useFormStore();
	const { id } = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const [isCompleted, setIsCompleted] = useState<boolean>(
		location.state?.isSubmissionCompleted,
	);

	// Watch assetLocation to force re-render when it changes (for conditional unique fields)
	// This ensures custom fields (roomNumber, officeNumber) appear/disappear based on asset location selection
	const assetLocation = Form.useWatch("assetLocation", form);

	// Watch location_id and location fields to get real-time values for draft saving
	const watchedLocationId = Form.useWatch("location_id", form);
	const watchedLocation = Form.useWatch("location", form);

	// Endpoint response caching
	const [endpointCache, setEndpointCache] = useState<Record<string, unknown>>(
		{},
	);
	const requestCache = useRef<Record<string, Promise<any>>>({});

	// Preview logic
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewImage, setPreviewImage] = useState("");
	const [previewTitle, setPreviewTitle] = useState("");
	const [draftDisabled, setDraftDisabled] = useState(false);

	// Reducer
	const [state, dispatch] = useReducer(formReducer, initialState);
	const { fieldOptions } = state;

	// Internal data load
	const [internalDataLoaded, setInternalDataLoaded] = useState(false);
	const [shouldAutoSave, setShouldAutoSave] = useState(false);

	// Plugin UI
	const [pluginEnhancements, setPluginEnhancements] =
		useState<PluginEnhancementsState>({});

	// Track which fields are highlighted as missing required fields
	const [highlightedFields, setHighlightedFields] = useState<Set<string>>(
		new Set(),
	);

	// Location display state
	const [locationDisplayText, setLocationDisplayText] = useState<string>("");
	// Cache location data to avoid repeated API calls
	const locationCache = useRef<Record<string, string>>({});
	const lastLocationIdRef = useRef<string | number | null>(null);

	const formKey = useMemo(() => location.pathname, [location.pathname]);

	/** Fetch and format location display text with caching */
	const fetchLocationDisplayText = useCallback(async (locationId: string) => {
		// Check cache first
		if (locationCache.current[locationId]) {
			setLocationDisplayText(locationCache.current[locationId]);
			return;
		}

		try {
			const tkn = await auth?.currentUser?.getIdToken();
			const url = `${VITE_CORE_API_URL}${CORE_URL}/organization/location/${locationId}`;
			const response = await fetch(url, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${tkn || ""}`,
				},
			});

			if (response.status === 200) {
				const responseData = await response.json();
				// Core API returns { success: true, data: location }
				const locationData = responseData?.data || responseData;
				const name = locationData?.name || "";
				const address1 = locationData?.address1 || "";
				const displayText =
					name || address1
						? `${name}${name && address1 ? ": " : ""}${address1}`
						: "Location";

				// Cache the result
				locationCache.current[locationId] = displayText;
				setLocationDisplayText(displayText);
			} else {
				setLocationDisplayText("Location");
				locationCache.current[locationId] = "Location";
			}
		} catch (error) {
			console.error("Error fetching location:", error);
			setLocationDisplayText("Location");
			locationCache.current[locationId] = "Location";
		}
	}, []);

	/** Clears all local state. */
	const clearState = useCallback(() => {
		saveProgressDebounced.cancel();
		setDraftId(undefined);
		setFormData({});
		setShouldAutoSave(false);
		setFormConfig(null);
		setFormName("");
		setFormLogo("");
		setPreviewVisible(false);
		setPreviewImage("");
		setPreviewTitle("");
		dispatch({ type: "SET_CURRENT_STEP", payload: 0 });
		dispatch({ type: "SET_FIELD_OPTIONS", payload: {} });
		dispatch({ type: "SET_DYNAMIC_LIST_STATE", payload: {} });
		form.resetFields();
		setInternalDataLoaded(false);
		setPluginEnhancements({});
		setHighlightedFields(new Set());
		setLocationDisplayText("");
		// Clear location cache and refs
		locationCache.current = {};
		lastLocationIdRef.current = null;
	}, [form, setDraftId, setFormData]);

	useEffect(() => {
		// If the route changes, reset form
		clearState();
	}, [location.pathname, clearState]);

	/** Update location display text when location changes - only fetch if locationId actually changed */
	useEffect(() => {
		// Use the watched form field values (not the Zustand store values)
		// This ensures we only display the location that's actually in the form fields
		const locationId = watchedLocationId || watchedLocation;

		// Only fetch if locationId actually changed
		if (
			locationId &&
			(typeof locationId === "string" || typeof locationId === "number")
		) {
			const locationIdStr = locationId.toString();
			if (lastLocationIdRef.current !== locationIdStr) {
				lastLocationIdRef.current = locationIdStr;
				fetchLocationDisplayText(locationIdStr);
			}
		} else {
			if (lastLocationIdRef.current !== null) {
				lastLocationIdRef.current = null;
				setLocationDisplayText("");
			}
		}
	}, [watchedLocationId, watchedLocation, fetchLocationDisplayText]);

	/**
	 * 1. Pre-load any forcibly disabled fields from data
	 * 2. Compute if needed
	 */
	useEffect(() => {
		if (!formConfig || !internalDataLoaded) return;

		// Force-compute on mount
		const initialVals = form.getFieldsValue(true);
		const newVals = computeAllFields(initialVals, formConfig);
		if (newVals !== initialVals) {
			form.setFieldsValue(newVals);
			setFormData(newVals);
		}
	}, [formConfig, internalDataLoaded, form, setFormData]);

	/**
	 * Watch for manufacturer date decoding opportunities
	 * This triggers when both manufacturer and serial number are populated
	 */
	useEffect(() => {
		// Only proceed if we have both manufacturer and serial number, but no manufacturer date
		if (
			formData.manufacturer &&
			formData.serialNumber &&
			!formData.manufacturerDate
		) {
			// Get the manufacturer name from the field options
			let manufacturerName = formData.manufacturer;
			if (
				typeof formData.manufacturer === "number" ||
				typeof formData.manufacturer === "string"
			) {
				const manufacturerOptions = state.fieldOptions.manufacturer || [];
				const manufacturerOption = manufacturerOptions.find(
					(opt: any) =>
						opt.value === formData.manufacturer ||
						opt.value === String(formData.manufacturer),
				);
				if (manufacturerOption?.label) {
					manufacturerName = manufacturerOption.label;
				}
			}

			// Check if this manufacturer has a known decoder
			if (
				typeof manufacturerName === "string" &&
				hasManufacturerDecoder(manufacturerName)
			) {
				try {
					const decodedDate = decodeManufacturerDate(
						manufacturerName,
						String(formData.serialNumber),
					);
					if (decodedDate) {
						// Convert Date to dayjs for the form
						const decodedDateDayjs = dayjs(decodedDate);

						// Update the form with the decoded date - preserve all existing data
						const updatedData = {
							...formData,
							manufacturerDate: decodedDateDayjs,
						};

						// Update the form data state first
						setFormData(updatedData);

						// Then update the form field to ensure synchronization
						form.setFieldsValue({ manufacturerDate: decodedDateDayjs });

						// Ensure photo fields maintain their file list binding
						const photoFields = [
							"equipmentPhoto",
							"manufacturerPlatePhoto",
							"assetTagPhoto",
						];
						photoFields.forEach((fieldName) => {
							if ((updatedData as any)[fieldName]) {
								form.setFieldsValue({
									[fieldName]: (updatedData as any)[fieldName],
								});
							}
						});

						// Show success message
						message.success(
							`Auto-decoded manufacturer date: ${decodedDateDayjs.format("MM/DD/YYYY")}`,
						);
					}
				} catch (error) {
					console.warn("Error decoding manufacturer date:", error);
					message.warning(
						"Unable to decode manufacturer date from serial number",
					);
				}
			}
		}
	}, [
		formData.manufacturer,
		formData.serialNumber,
		formData.manufacturerDate,
		state.fieldOptions.manufacturer,
		form,
		setFormData,
	]);

	useEffect(() => {
		const formIdFromState = (location.state?.formId as string) || id || null;

		const fetchForm = async () => {
			try {
				// Potential partial submission data from location.state
				let parsed: any;
				if (location.state?.inputs) {
					parsed = location.state.inputs;
				} else if (location.state?.responseData) {
					parsed = location.state.responseData;
				}

				// Handle form initialization
				// formIdFromState is the organization-specific form ID (e.g., 58)
				// We need to find the template form ID (3 or 7) from the organization form
				if (formIdFromState) {
					const orgFormIdNum = Number.parseInt(formIdFromState, 10);

					// Find the organization form to get the template form ID
					const orgForm = user?.organization?.forms?.find(
						(f: any) =>
							f.id === orgFormIdNum ||
							f.formId === orgFormIdNum ||
							f.form_id === orgFormIdNum,
					);

					if (!orgForm) {
						setInternalDataLoaded(true);
						return;
					}

					// Get the template form ID (3, 7, etc.)
					const templateFormId = orgForm?.formId || orgForm?.form_id;
					const templateFormIdNum =
						typeof templateFormId === "number"
							? templateFormId
							: templateFormId
								? Number.parseInt(String(templateFormId), 10)
								: 0;

					if (!templateFormIdNum || templateFormIdNum === 0) {
						setInternalDataLoaded(true);
						return;
					}

					// For template form ID 3, use hardcoded form config (not fetched from anywhere)
					// This ensures consistent form structure across all organizations
					if (templateFormIdNum === 3) {
						// Get logo from user store
						const orgLogo = user?.organization?.config?.logo_url || "";

						/**
						 * Get custom fields from organization form config
						 * orgForm.config can be either:
						 * 1. An array of unique fields directly (current format)
						 * 2. An object with uniqueFields property (legacy format)
						 */
						let customFields: Array<{
							name: string;
							label: string;
							type: string;
							required?: boolean;
							placeholder?: string;
							options?: string[];
							conditional?: {
								dependsOn: string;
								operator?: string;
								value?: unknown;
								values?: unknown[];
							};
						}> | null = null;

						if (Array.isArray(orgForm?.config)) {
							// Case 1: config is an array of fields directly
							customFields = orgForm.config as Array<{
								name: string;
								label: string;
								type: string;
								required?: boolean;
								placeholder?: string;
								options?: string[];
								conditional?: {
									dependsOn: string;
									operator?: string;
									value?: unknown;
									values?: unknown[];
								};
							}>;
						} else if (
							orgForm?.config &&
							typeof orgForm.config === "object" &&
							Array.isArray(orgForm.config.uniqueFields)
						) {
							// Case 2: config is an object with uniqueFields property (legacy format)
							customFields = orgForm.config.uniqueFields as Array<{
								name: string;
								label: string;
								type: string;
								required?: boolean;
								placeholder?: string;
								options?: string[];
								conditional?: {
									dependsOn: string;
									operator?: string;
									value?: unknown;
									values?: unknown[];
								};
							}>;
						}

						// Use hardcoded form config for form ID 3 (without custom fields yet)
						const baseConfig = getFormId3Config(orgLogo);

						setFormName(baseConfig.config.formName || "Inventory & Tagging");
						setFormLogo(baseConfig.config.logo || "");

						// For form ID 3, extract refrigerant data before merging (only if not already in original config)
						let preservedRefrigerantData: any = {};

						if (parsed?.sections) {
							// Extract refrigerant data from parsed submission
							parsed.sections.forEach((section: any) => {
								const fields = section.fields || section.questions || [];
								fields.forEach((field: any) => {
									if (
										field.name === "refrigerant" &&
										field.value !== undefined
									) {
										preservedRefrigerantData.refrigerant = field.value;
									}
									if (
										field.name === "refrigerantCharge" &&
										field.value !== undefined
									) {
										preservedRefrigerantData.refrigerantCharge = field.value;
									}
									if (
										field.name === "refrigerantUnit" &&
										field.value !== undefined
									) {
										preservedRefrigerantData.refrigerantUnit = field.value;
									}
								});
							});
						}

						// Restructure form config for form ID 3 (this creates the hardcoded 5-page structure)
						const configToUse = restructureFormConfig(baseConfig);

						// If we got partial data => merge it into the config AFTER restructuring
						if (parsed?.sections) {
							mergeSubmissionIntoConfig(
								configToUse.config.sections,
								parsed.sections,
							);
						}

						// Now preprocess, prepare computed, and build defaults
						preprocessSections(configToUse);
						prepareComputedFields(configToUse);

						// Append custom fields AFTER restructuring (so they're preserved)
						// This ensures custom fields appear before the Summary section
						const configWithCustomFields = appendCustomFieldsToFormConfig(
							configToUse,
							customFields,
						);

						// Build initialValues from the merged config:
						const { initialValues, dynamicCounts } = parsed?.sections
							? buildInitialValuesFromParsedInputs({
									...parsed,
									sections: configWithCustomFields.config.sections,
								})
							: {
									initialValues: buildInitialValuesFromFormConfig(
										configWithCustomFields,
									),
									dynamicCounts: {},
								};

						// Add our preserved location data if available
						// Only restore location if coming from "Submit & Enter New" (location.state has locationId)
						// When navigating from /forms page, location.state.locationId will be undefined, so location will be cleared
						const locId = location.state?.locationId;
						if (locId) {
							initialValues.location_id = locId;
							initialValues.location = locId;
						}

						// For form ID 3, handle legacy submissions that don't have conditional fields
						if (parsed?.sections) {
							// Add preserved refrigerant data back to initialValues (only if we extracted it)
							if (Object.keys(preservedRefrigerantData).length > 0) {
								Object.assign(initialValues, preservedRefrigerantData);
							}

							// Infer hasManufacturerPlate from presence of manufacturer plate data
							if (
								!initialValues.hasManufacturerPlate ||
								initialValues.hasManufacturerPlate === "" ||
								initialValues.hasManufacturerPlate === null
							) {
								const hasManufacturerData = !!(
									(initialValues.modelNumber &&
										initialValues.modelNumber !== "") ||
									(initialValues.serialNumber &&
										initialValues.serialNumber !== "") ||
									(initialValues.manufacturerDate &&
										initialValues.manufacturerDate !== "") ||
									(initialValues.manufacturerPlatePhoto &&
										Array.isArray(initialValues.manufacturerPlatePhoto) &&
										initialValues.manufacturerPlatePhoto.length > 0)
								);
								initialValues.hasManufacturerPlate = hasManufacturerData
									? "yes"
									: "no";
							}

							// Infer hasAssetTag from presence of asset tag data
							if (
								!initialValues.hasAssetTag ||
								initialValues.hasAssetTag === "" ||
								initialValues.hasAssetTag === null
							) {
								const hasAssetTagData = !!(
									(initialValues.assetTag && initialValues.assetTag !== "") ||
									(initialValues.assetTagPhoto &&
										Array.isArray(initialValues.assetTagPhoto) &&
										initialValues.assetTagPhoto.length > 0)
								);
								initialValues.hasAssetTag = hasAssetTagData ? "yes" : "no";
							}

							// Infer hasRefrigerationDetails from presence of refrigerant data
							if (
								!initialValues.hasRefrigerationDetails ||
								initialValues.hasRefrigerationDetails === "" ||
								initialValues.hasRefrigerationDetails === null
							) {
								const hasRefrigerationData = !!(
									(initialValues.refrigerant &&
										initialValues.refrigerant !== "") ||
									(initialValues.refrigerantCharge &&
										initialValues.refrigerantCharge !== "") ||
									(initialValues.refrigerantUnit &&
										initialValues.refrigerantUnit !== "")
								);
								initialValues.hasRefrigerationDetails = hasRefrigerationData
									? "yes"
									: "no";
							}
						}

						// For form ID 3, ensure conditional fields are set to defaults for new submissions
						if (!parsed?.sections) {
							if (initialValues.hasManufacturerPlate === undefined) {
								initialValues.hasManufacturerPlate = "no";
							}
							if (initialValues.hasAssetTag === undefined) {
								initialValues.hasAssetTag = "no";
							}
							if (initialValues.hasRefrigerationDetails === undefined) {
								initialValues.hasRefrigerationDetails = "no";
							}

							// Don't auto-populate manufacturer and model - leave empty for user to fill
							// Remove any defaults that might have been set
							if (initialValues.hasManufacturerPlate === "no") {
								if (initialValues.manufacturer === 2719) {
									delete initialValues.manufacturer;
								}
								if (initialValues.modelNumber === "0") {
									delete initialValues.modelNumber;
								}
							}
						}

						setFormData(initialValues);
						form.setFieldsValue(initialValues);
						setFormConfig(configWithCustomFields);

						// (Optional) fetch dataSources for the fields:
						const allFs = getAllFields(configWithCustomFields.config.sections);
						const promises = allFs
							.filter(
								(f) =>
									f.dataSource?.type === "api" ||
									(f.options && Array.isArray(f.options)),
							)
							.map((f) => fetchFieldData(f, initialValues));
						await Promise.all(promises);

						// If any dynamic list counts
						if (Object.keys(dynamicCounts).length > 0) {
							dispatch({
								type: "SET_DYNAMIC_LIST_STATE",
								payload: dynamicCounts,
							});
						}

						setShouldAutoSave(
							user?.organization.config?.auto_save_drafts === true,
						);
						setInternalDataLoaded(true);
						return;
					}

					// For template form ID 7 or other forms, fetch from API using template form ID
					// getFormData already handles form ID 3 correctly (returns "Inventory & Tagging")
					await getFormData(
						user?.organization?.external_firebase_id!,
						templateFormIdNum,
						setFormName,
						setFormLogo,
						async (dbConfig: any) => {
							if (!dbConfig) {
								setFormConfig({ config: { sections: [] } });
								setInternalDataLoaded(true);
								return;
							}

							// Only restructure for form ID 3, otherwise use original config
							const configToUse =
								templateFormIdNum === 3
									? restructureFormConfig(dbConfig)
									: dbConfig;

							// If we have parsed submission data, merge it into the config BEFORE preprocessing
							if (parsed?.sections) {
								mergeSubmissionIntoConfig(
									configToUse.config.sections,
									parsed.sections,
								);
							}

							// Standard preprocessing
							preprocessSections(configToUse);
							prepareComputedFields(configToUse);

							// Append custom fields from organization form config if they exist
							// Only for form ID 3 - get uniqueFields from the org form
							// orgForm.config can be either:
							// 1. An array of unique fields directly (current format)
							// 2. An object with uniqueFields property (legacy format)
							let customFields: Array<{
								name: string;
								label: string;
								type: string;
								required?: boolean;
								placeholder?: string;
								options?: string[];
								conditional?: {
									dependsOn: string;
									operator?: string;
									value?: unknown;
									values?: unknown[];
								};
							}> | null = null;

							if (templateFormIdNum === 3) {
								if (Array.isArray(orgForm?.config)) {
									// Case 1: config is an array of fields directly
									customFields = orgForm.config as Array<{
										name: string;
										label: string;
										type: string;
										required?: boolean;
										placeholder?: string;
										options?: string[];
										conditional?: {
											dependsOn: string;
											operator?: string;
											value?: unknown;
											values?: unknown[];
										};
									}>;
								} else if (
									orgForm?.config &&
									typeof orgForm.config === "object" &&
									Array.isArray(orgForm.config.uniqueFields)
								) {
									// Case 2: config is an object with uniqueFields property (legacy format)
									customFields = orgForm.config.uniqueFields as Array<{
										name: string;
										label: string;
										type: string;
										required?: boolean;
										placeholder?: string;
										options?: string[];
										conditional?: {
											dependsOn: string;
											operator?: string;
											value?: unknown;
											values?: unknown[];
										};
									}>;
								}
							}
							const configWithCustomFields = appendCustomFieldsToFormConfig(
								configToUse,
								customFields,
							);

							// Build initialValues - if we have parsed data, use it; otherwise build from config
							const { initialValues, dynamicCounts } = parsed?.sections
								? buildInitialValuesFromParsedInputs({
										...parsed,
										sections: configWithCustomFields.config.sections,
									})
								: {
										initialValues: buildInitialValuesFromFormConfig(
											configWithCustomFields,
										),
										dynamicCounts: {},
									};

							// Add our preserved location data if available
							// Only restore location if coming from "Submit & Enter New" (location.state has locationId)
							// When navigating from /forms page, location.state.locationId will be undefined, so location will be cleared
							const locId = location.state?.locationId;
							if (locId) {
								initialValues.location_id = locId;
								initialValues.location = locId;
							}

							// For form ID 3, ensure conditional fields are set to defaults for new submissions
							if (templateFormIdNum === 3) {
								if (initialValues.hasManufacturerPlate === undefined) {
									initialValues.hasManufacturerPlate = "no";
								}
								if (initialValues.hasAssetTag === undefined) {
									initialValues.hasAssetTag = "no";
								}
								if (initialValues.hasRefrigerationDetails === undefined) {
									initialValues.hasRefrigerationDetails = "no";
								}

								// Don't auto-populate manufacturer and model - leave empty for user to fill
								if (initialValues.hasManufacturerPlate === "no") {
									if (initialValues.manufacturer === 2719) {
										delete initialValues.manufacturer;
									}
									if (initialValues.modelNumber === "0") {
										delete initialValues.modelNumber;
									}
								}
							}

							setFormData(initialValues);
							form.setFieldsValue(initialValues);
							setFormConfig(configWithCustomFields);
							setInternalDataLoaded(true);
						},
						user?.organization?.forms,
					);
					setShouldAutoSave(
						user?.organization.config?.auto_save_drafts === true,
					);
					setInternalDataLoaded(true);
				} else {
					// If no formId but we do have parsed data => build direct config
					if (parsed?.sections) {
						setFormLogo(
							parsed.logo || user?.organization?.config?.logo_url || "",
						);
						setFormName(parsed.title || "Form");

						const directConfig: DFSFormConfig = {
							config: { sections: parsed.sections },
						};
						preprocessSections(directConfig);
						prepareComputedFields(directConfig);

						// Append custom fields from organization form config if they exist
						// Only for form ID 3 - find form_id 3 and get its uniqueFields
						const formId = Number.parseInt(formIdFromState || "0", 10);
						const formId3 =
							formId === 3
								? user?.organization?.forms?.find(
										(f: any) => f.formId === 3 || f.form_id === 3,
									)
								: null;
						const customFields =
							formId === 3 && Array.isArray(formId3?.config?.uniqueFields)
								? (formId3.config.uniqueFields as Array<{
										name: string;
										label: string;
										type: string;
										required?: boolean;
										placeholder?: string;
										options?: string[];
										conditional?: {
											dependsOn: string;
											operator?: string;
											value?: unknown;
											values?: unknown[];
										};
									}>)
								: null;
						const directConfigWithCustomFields = appendCustomFieldsToFormConfig(
							directConfig,
							customFields,
						);

						const { initialValues, dynamicCounts } =
							buildInitialValuesFromParsedInputs({
								...parsed,
								sections: directConfigWithCustomFields.config.sections,
							});

						// For form ID 3, handle legacy submissions that don't have conditional fields
						const directFormId = Number.parseInt(formIdFromState || "0", 10);
						if (directFormId === 3 && parsed?.sections) {
							// Infer hasManufacturerPlate from presence of manufacturer plate data
							// Check for undefined, null, empty string, or any falsy value to ensure
							// existing submissions with missing conditional fields get properly inferred
							if (
								!initialValues.hasManufacturerPlate ||
								initialValues.hasManufacturerPlate === "" ||
								initialValues.hasManufacturerPlate === null
							) {
								// Determine if manufacturer plate data exists by checking for any populated manufacturer fields
								const hasManufacturerData = !!(
									(initialValues.modelNumber &&
										initialValues.modelNumber !== "") ||
									(initialValues.serialNumber &&
										initialValues.serialNumber !== "") ||
									(initialValues.manufacturerDate &&
										initialValues.manufacturerDate !== "") ||
									(initialValues.manufacturerPlatePhoto &&
										Array.isArray(initialValues.manufacturerPlatePhoto) &&
										initialValues.manufacturerPlatePhoto.length > 0)
								);
								// Set the conditional field value based on the presence of actual data
								initialValues.hasManufacturerPlate = hasManufacturerData
									? "yes"
									: "no";
							}

							// Infer hasAssetTag from presence of asset tag data
							// Check for undefined, null, empty string, or any falsy value to ensure
							// existing submissions with missing conditional fields get properly inferred
							if (
								!initialValues.hasAssetTag ||
								initialValues.hasAssetTag === "" ||
								initialValues.hasAssetTag === null
							) {
								// Determine if asset tag data exists by checking for any populated asset tag fields
								const hasAssetTagData = !!(
									(initialValues.assetTag && initialValues.assetTag !== "") ||
									(initialValues.assetTagPhoto &&
										Array.isArray(initialValues.assetTagPhoto) &&
										initialValues.assetTagPhoto.length > 0)
								);
								// Set the conditional field value based on the presence of actual data
								initialValues.hasAssetTag = hasAssetTagData ? "yes" : "no";
							}

							// Infer hasRefrigerationDetails from presence of refrigerant data
							// Check for undefined, null, empty string, or any falsy value to ensure
							// existing submissions with missing conditional fields get properly inferred
							if (
								!initialValues.hasRefrigerationDetails ||
								initialValues.hasRefrigerationDetails === "" ||
								initialValues.hasRefrigerationDetails === null
							) {
								// Determine if refrigeration data exists by checking for any populated refrigerant fields
								const hasRefrigerationData = !!(
									(initialValues.refrigerant &&
										initialValues.refrigerant !== "") ||
									(initialValues.refrigerantCharge &&
										initialValues.refrigerantCharge !== "") ||
									(initialValues.refrigerantUnit &&
										initialValues.refrigerantUnit !== "")
								);
								// Set the conditional field value based on the presence of actual data
								initialValues.hasRefrigerationDetails = hasRefrigerationData
									? "yes"
									: "no";
							}
						}

						setFormData(initialValues);
						form.setFieldsValue(initialValues);
						setFormConfig(directConfigWithCustomFields);

						// fetch dataSources if needed
						const allFs = getAllFields(
							directConfigWithCustomFields.config.sections,
						);
						const promises = allFs
							.filter(
								(f) =>
									f.dataSource?.type === "api" ||
									(f.options && Array.isArray(f.options)),
							)
							.map((f) => fetchFieldData(f, initialValues));
						await Promise.all(promises);

						if (Object.keys(dynamicCounts).length > 0) {
							dispatch({
								type: "SET_DYNAMIC_LIST_STATE",
								payload: dynamicCounts,
							});
						}
						setShouldAutoSave(
							user?.organization?.config?.auto_save_drafts === true,
						);
						setInternalDataLoaded(true);
					} else {
						// Fallback: no formId, no parsed data => blank
						setFormConfig({ config: { sections: [] } });
						setInternalDataLoaded(true);
					}
				}
			} catch (error) {
				console.error("Error initializing form:", error);
				message.error("Error initializing form.");
			}
		};

		fetchForm();
	}, [id, location.state, user?.organization?.forms, form, setFormData]);

	/**
	 * Fetch field data if needed (API, etc.)
	 */
	const fetchFieldData = useCallback(
		async (field: DFSFormField, values: Record<string, any> = {}) => {
			if (!field.name) return;
			dispatch({ type: "SET_LOADING_FIELD", payload: { [field.name]: true } });

			try {
				let data: DFSFieldOption[] = [];

				if (field.dataSource?.type === "api" && field.dataSource.endpoint) {
					const endpoint = field.dataSource.endpoint;

					let responseData: unknown;
					if (endpointCache[endpoint]) {
						responseData = endpointCache[endpoint];
					} else {
						if (!requestCache.current[endpoint]) {
							requestCache.current[endpoint] = (async () => {
								try {
									const params: Record<string, any> = {};
									if (field?.dataSource?.dependencies) {
										field.dataSource.dependencies.forEach((dep) => {
											params[dep] = values[dep];
										});
									}
									let fullEndpoint = endpoint;
									// Handle endpoint URL construction:
									// - Full URLs (http:// or https://) are used as-is
									// - Core API endpoints are prepended with Core API URL
									// - Other relative paths use Alfred service URL (legacy)
									const isFullUrl =
										endpoint.startsWith("http://") ||
										endpoint.startsWith("https://");
									const isCoreApiEndpoint =
										endpoint.includes(VITE_CORE_API_URL) ||
										endpoint.includes(CORE_URL);

									if (!isFullUrl && !isCoreApiEndpoint) {
										// Only prepend Alfred URL for relative paths that aren't Core API
										const baseUrl = VITE_ALFRED_SERVICE_URL || "";
										const endpointPath = endpoint.replace(
											/^https?:\/\/[^/]+/,
											"",
										);
										fullEndpoint = `${baseUrl}${endpointPath}`;
									} else if (isCoreApiEndpoint && !isFullUrl) {
										// If it's a Core API endpoint but not full URL, prepend Core API URL
										fullEndpoint = `${VITE_CORE_API_URL}${endpoint}`;
									}

									const tkn = await auth?.currentUser?.getIdToken();
									const resp = await axios.get(fullEndpoint, {
										params,
										headers: { Authorization: `Bearer ${tkn || ""}` },
									});

									setEndpointCache((prev) => ({
										...prev,
										[endpoint]: resp.data,
									}));
									return resp.data;
								} finally {
									delete requestCache.current[endpoint];
								}
							})();
						}
						responseData = await requestCache.current[endpoint];
					}

					if (responseData) {
						let fieldData = responseData;
						if (field.dataSource?.metadata?.responsePath) {
							const paths = (
								field.dataSource.metadata.responsePath as string
							).split(".");
							for (const path of paths) {
								fieldData = (fieldData as any)?.[path];
								if (!fieldData) break;
							}
						}
						if (fieldData && field.dataSource.metadata) {
							const metadata = field.dataSource.metadata as any;
							const labelKey = metadata.label || "";
							const valueKey = metadata.value || "";
							const arr = Array.isArray(fieldData) ? fieldData : [fieldData];
							data = arr.map((it: any) => ({
								label: labelKey ? it[labelKey] : it,
								value: valueKey ? it[valueKey] : it,
							}));
						} else {
							data = Array.isArray(fieldData) ? fieldData : [fieldData];
						}
					}
				} else if (field.dataSource?.type === "static") {
					data = field.dataSource.options || [];
				} else if (field.options && Array.isArray(field.options)) {
					// Handle fields with direct static options (like storeName and storeType)
					data = field.options;
				} else if (
					field.dataSource?.type === "function" &&
					field.dataSource.fetchFunction
				) {
					data = await field.dataSource.fetchFunction(values);
				}

				const filteredData = getFilteredOptions(field, data, values);
				dispatch({
					type: "SET_FIELD_OPTIONS",
					payload: { [field.name]: filteredData || data || [] },
				});
			} catch (err) {
				console.error("Failed fetching field data:", err);
			} finally {
				dispatch({
					type: "SET_LOADING_FIELD",
					payload: { [field.name]: false },
				});
			}
		},
		[endpointCache],
	);

	/** Debounced auto-save to drafts. */
	const saveProgressDebounced = useCallback(
		debounce(
			async (props: {
				formData: Record<string, any>;
				formId: string | null;
				existingDraftId: number | string | null;
			}) => {
				const { formData, formId, existingDraftId } = props;
				if (!formId || !formData || !formConfig) return;

				// Convert organization-specific form ID to global form ID (3 or 7)
				const orgFormIdNum = Number.parseInt(formId, 10);
				const orgForm = user?.organization?.forms?.find(
					(f: any) =>
						f.id === orgFormIdNum ||
						f.formId === orgFormIdNum ||
						f.form_id === orgFormIdNum,
				);

				// Get the global form ID (2, 3, 4, 7, etc.)
				const globalFormId = orgForm?.formId || orgForm?.form_id;
				const globalFormIdNum =
					typeof globalFormId === "number"
						? globalFormId
						: globalFormId
							? Number.parseInt(String(globalFormId), 10)
							: null;

				// Validate that we have a valid global form ID
				// Valid form IDs: 2 (Handyman Checklist), 3 (Inventory & Tagging), 4 (QC Checklist), 7 (Property Condition Assessment)
				const validFormIds = [2, 3, 4, 7];
				if (!globalFormIdNum || !validFormIds.includes(globalFormIdNum)) {
					console.error(
						`Auto-save failed: Invalid global form ID. Expected one of [${validFormIds.join(", ")}], but found: ${globalFormIdNum || "unknown"}`,
					);
					return;
				}

				const locId =
					formData.location_id ||
					formData.location ||
					location.state?.locationId;
				if (!shouldAutoSave || !locId || draftDisabled) return;

				const dataToSave = buildSubmissionData(formConfig, formData);
				if (!dataToSave) return;

				try {
					if (!existingDraftId) {
						const newDraftId = await createDraft({
							location_id: locId,
							form_id: globalFormIdNum, // Use global form ID (3 or 7)
							data: { ...dataToSave },
						});
						setDraftId(newDraftId);
						message.success("Draft saved successfully.");
					} else {
						await updateDraft({
							draft_id: existingDraftId,
							data: { ...dataToSave },
							form_id: globalFormIdNum, // Use global form ID (3 or 7)
						});
						message.success("Draft updated successfully.");
					}
				} catch {
					message.error("Failed to save draft.");
				}
			},
			1500,
		),
		[
			formConfig,
			location,
			shouldAutoSave,
			draftDisabled,
			setDraftId,
			user?.organization?.forms,
		],
	);

	/** Called whenever form values change => auto-save if enabled. */
	function attemptAutoSave(mergedData: Record<string, any>) {
		if (!formConfig || !shouldAutoSave || draftDisabled || isCompleted) return;
		const formIdFromLocation = (location.state?.formId as string) || id || null;
		saveProgressDebounced({
			formData: mergedData,
			formId: formIdFromLocation,
			existingDraftId: draftId || null,
		});
	}

	/** For image preview (antd Upload) */
	async function handlePreview(file: UploadFile) {
		// Prioritize thumbUrl (data URL for converted images) over remote URLs for immediate preview
		let url =
			file.thumbUrl ||
			file.response?.thumbUrl ||
			file.url ||
			file.response?.url ||
			file.preview;

		if (!url && file.originFileObj) {
			try {
				url = (await getBase64(file.originFileObj)) as string;
				file.preview = url;
			} catch (error) {
				console.error("Error generating preview:", error);
				// If we can't generate a preview (e.g., for HEIC files), fallback to any URL
				url = file.url || file.response?.url || "";
			}
		}

		if (url) {
			setPreviewImage(url);
			setPreviewVisible(true);
			setPreviewTitle(file.name || url.substring(url.lastIndexOf("/") + 1));
		}
	}
	function getBase64(file: Blob): Promise<string | ArrayBuffer | null> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = (err) => reject(err);
		});
	}
	function normFile(e: any): UploadFile[] {
		if (!e) return [];
		return Array.isArray(e) ? e : e.fileList;
	}

	/**
	 * Evaluate conditionals => should we show the field?
	 *
	 * Handles conditional field visibility based on form values.
	 * Supports various operators: equals, notEquals, in, notIn, greaterThan, lessThan, contains.
	 * Type normalization ensures numeric comparisons work correctly (e.g., "919" === 919).
	 */
	function evaluateCondition(
		field: DFSFormField,
		frm: FormInstance,
		fieldOptions: Record<string, DFSFieldOption[]> = {},
		config: DFSFormConfig | null = null,
	): boolean {
		if (!field.conditional) return true;
		const { dependsOn, operator = "equals", value, values } = field.conditional;

		// If dependsOn is missing, treat as no condition (always show)
		if (!dependsOn) return true;

		const allVals = frm.getFieldsValue(true);

		// Handle nested field paths (e.g., "items.0.name" depends on "items.0.category")
		let actualPath = dependsOn;
		if (!dependsOn.includes(".") && field.name && field.name.includes(".")) {
			const parts = field.name.split(".");
			parts.pop();
			const prefix = parts.join(".");
			actualPath = `${prefix}.${dependsOn}`;
		}

		let dv = allVals[actualPath];
		// Special handling for empty string comparisons
		if (
			operator === "notEquals" &&
			value === "" &&
			(dv === undefined || dv === null)
		) {
			dv = "";
		}

		/**
		 * Normalize types for comparison
		 * Converts numeric strings to numbers to ensure proper comparison
		 * (e.g., form value "919" should match conditional value 919)
		 */
		const normalizeValue = (val: any): any => {
			if (val === null || val === undefined) return val;
			// If value is a number, return as-is
			if (typeof val === "number") return val;
			// If value is a string that can be converted to a number (and not empty), do so
			if (typeof val === "string" && val !== "" && !isNaN(Number(val))) {
				return Number(val);
			}
			// Otherwise return as string
			return String(val);
		};

		const normalizedDv = normalizeValue(dv);
		const normalizedValue = normalizeValue(value);

		// Evaluate based on operator
		switch (operator) {
			case "equals":
				return normalizedDv === normalizedValue;
			case "notEquals":
				return normalizedDv !== normalizedValue;
			case "in":
				if (!Array.isArray(values)) return false;
				return values.some((v) => normalizeValue(v) === normalizedDv);
			case "notIn":
				if (!Array.isArray(values)) return true;
				return !values.some((v) => normalizeValue(v) === normalizedDv);
			case "greaterThan":
				return (
					typeof normalizedDv === "number" &&
					typeof normalizedValue === "number" &&
					normalizedDv > normalizedValue
				);
			case "lessThan":
				return (
					typeof normalizedDv === "number" &&
					typeof normalizedValue === "number" &&
					normalizedDv < normalizedValue
				);
			case "contains":
				return Array.isArray(dv) && dv.includes(value);
			default:
				return true;
		}
	}

	/** Build validation rules => skip 'required' if field.disabled. */
	function buildValidationRules(field: DFSFormField) {
		const rules: any[] = [];
		if (!field.validation) return rules;

		const arr = Array.isArray(field.validation)
			? field.validation
			: [field.validation];
		for (const vObj of arr) {
			const { pattern, minLength, maxLength, min, max, message, required } =
				vObj;
			if (required && !field.disabled) {
				rules.push({
					required: true,
					message: message || `${field.label || "Field"} is required`,
				});
			}
			if (pattern) {
				rules.push({
					pattern: new RegExp(pattern),
					message: message || "Invalid format",
				});
			}
			if (typeof minLength === "number") {
				rules.push({
					min: minLength,
					message:
						message ||
						`${field.label || "Field"} must be at least ${minLength} characters`,
				});
			}
			if (typeof maxLength === "number") {
				rules.push({
					max: maxLength,
					message:
						message ||
						`${field.label || "Field"} must be at most ${maxLength} characters`,
				});
			}
			if (typeof min === "number") {
				rules.push({
					type: "number",
					min,
					message: message || `${field.label || "Field"} must be ≥ ${min}`,
				});
			}
			if (typeof max === "number") {
				rules.push({
					type: "number",
					max,
					message: message || `${field.label || "Field"} must be ≤ ${max}`,
				});
			}
		}
		return rules;
	}

	/** Renders a field (group/dynamicList => recursion) */
	function renderField(field: DFSFormField, frm: FormInstance): ReactNode {
		const fieldName = field.name || field.responseTag || field.key;
		if (!fieldName) return null;
		if (!evaluateCondition(field, frm, state.fieldOptions, formConfig))
			return null;

		if (field.type === "group" && field.fields) {
			return (
				<Fragment key={fieldName}>
					{field.fields.map((sf, idx) => {
						const subName = sf.name || sf.responseTag || sf.key;
						if (!subName) return null;
						const nested: DFSFormField = {
							...sf,
							name: `${fieldName}.${subName}`,
						};
						return (
							<Fragment key={`${fieldName}-${idx}`}>
								{renderField(nested, frm)}
							</Fragment>
						);
					})}
				</Fragment>
			);
		}

		if (field.type === "dynamicList" && field.fields) {
			const allVals = frm.getFieldsValue(true);
			const cnt = getDynamicListCount(allVals, field);

			return (
				<Fragment key={fieldName}>
					{Array.from({ length: cnt }).map((_, i) => (
						<div key={`${fieldName}-${i}`} className="mb-4">
							{field.label && (
								<Tag className="my-2" color="purple">
									{field.label}
									{field.tooltip && (
										<Tooltip
											title={renderTooltipContent(field.tooltip)}
											placement="top"
										>
											<InfoCircleOutlined
												style={{ marginLeft: 8, cursor: "pointer" }}
											/>
										</Tooltip>
									)}{" "}
									{i + 1}
								</Tag>
							)}
							{field?.fields?.map((subFld) => {
								const subBaseName =
									(subFld.name || subFld.responseTag || subFld.key || "")
										.split(".")
										.pop() || "";
								const indexedSub: DFSFormField = {
									...subFld,
									name: `${field.name}.${i}.${subBaseName}`,
								};
								return (
									<Fragment key={`${fieldName}-${i}-${indexedSub.name}`}>
										{renderField(indexedSub, frm)}
									</Fragment>
								);
							})}
						</div>
					))}
				</Fragment>
			);
		}

		return renderAtomicField(field, frm);
	}

	/** Shared icon for "loading" states. */
	const LoadingSuffix = () => (
		<LoadingOutlined style={{ fontSize: 14, color: "#1890ff" }} spin />
	);

	/** Render actual Input/Select/etc. */
	function renderAtomicField(
		field: DFSFormField,
		frm: FormInstance,
	): ReactNode {
		const fieldName = field.name || field.responseTag || field.key;
		const fieldPlaceholder = field.placeholder || "";
		if (!fieldName) return null;

		const rules = buildValidationRules(field);
		let formatFn: ((val: string) => string) | undefined;
		if (field.format?.formatFn) {
			try {
				formatFn = eval(`(${field.format.formatFn})`);
			} catch {
				/* ignore */
			}
		}

		const pluginUi = pluginEnhancements[fieldName] || {};
		const isLoading = Boolean(
			state.loadingFields[fieldName] || pluginUi.loading,
		);
		const fieldSuffix = isLoading ? (
			<LoadingSuffix />
		) : (
			(pluginUi.suffix ?? field.format?.inputSuffix)
		);

		// Check if field is highlighted as missing required
		const isHighlighted = highlightedFields.has(fieldName);
		const isRequired = rules.some((rule) => rule.required);

		// Determine validation status - prioritize highlighting over plugin UI, then loading
		const validateStatus = isHighlighted
			? "error"
			: isLoading
				? "validating"
				: pluginUi.validateStatus;
		const help = isHighlighted
			? `${field.label || "Field"} is required`
			: isLoading
				? "Processing OCR data..."
				: pluginUi.help;

		const commonProps = {
			name: fieldName,
			label: getFieldLabel(field),
			rules,
			hidden: field.hide,
			validateStatus,
			help,
			className: isLoading ? "field-loading" : undefined,
		} as const;

		// Skip button component - exclude location, asset tag, and decision questions (only for form ID 3)
		const isLocationField =
			fieldName === "location_id" || fieldName === "location";
		const isAssetTagField = fieldName === "assetTag";

		// Check if this is form ID 3 to apply special skip restrictions
		const formIdFromLocation = (location.state?.formId as string) || id || null;
		const isFormId3 = formIdFromLocation === "3";

		// For form ID 3, make all second page fields non-skippable
		const isSecondPageField =
			isFormId3 &&
			(fieldName === "equipmentPhoto" ||
				fieldName === "assetLocation" ||
				fieldName === "roomNumber" ||
				fieldName === "assetType" ||
				fieldName === "manufacturer" ||
				fieldName === "conditionRating" ||
				fieldName === "qsrStore" ||
				fieldName === "storeName" ||
				fieldName === "storeType");

		const isDecisionField =
			isFormId3 &&
			(fieldName === "hasManufacturerPlate" || fieldName === "hasAssetTag");
		const canSkip =
			!isLocationField &&
			!isAssetTagField &&
			!isDecisionField &&
			!isSecondPageField;

		const SkipButton =
			canSkip && (isRequired || field.disabled) ? (
				<div
					style={{
						position: "absolute",
						top: "-8px",
						right: "0px",
						zIndex: 10,
						backgroundColor: "#f3e8ff",
						padding: "4px 8px",
						borderRadius: "6px",
						border: "1px solid #e9d5ff",
					}}
				>
					<Checkbox
						checked={field.disabled || false}
						onChange={() => toggleFieldSkip(fieldName)}
						style={{ margin: 0 }}
					>
						Skip
					</Checkbox>
				</div>
			) : null;

		// Special handling for summary display field
		if (fieldName === "summaryDisplay") {
			const formValues = frm.getFieldsValue(true);

			// Helper function to get display label from field options
			const getDisplayLabel = (fieldName: string, value: any): string => {
				if (!value) return "Not provided";
				const options = state.fieldOptions[fieldName];
				if (options && Array.isArray(options)) {
					const option = options.find((opt) => opt.value === value);
					return option?.label || value.toString();
				}
				return value.toString();
			};

			// Check which fields exist in the current form config
			const allConfigFields = formConfig
				? getAllFields(formConfig.config.sections)
				: [];
			const hasWarrantyFields = allConfigFields.some(
				(f) => f.name && f.name.toLowerCase().includes("warranty"),
			);
			const hasRoomNumber = allConfigFields.some(
				(f) => f.name === "roomNumber",
			);
			const hasArchivedAssetTag = allConfigFields.some(
				(f) => f.name === "archivedAssetTag",
			);
			const hasStoreName = allConfigFields.some((f) => f.name === "storeName");
			const hasStoreType = allConfigFields.some((f) => f.name === "storeType");

			const SummaryCard = ({
				title,
				children,
			}: { title: string; children: React.ReactNode }) => (
				<div
					style={{
						backgroundColor: "#fff",
						border: "1px solid #e1e5e9",
						borderRadius: "8px",
						padding: "16px",
						marginBottom: "16px",
						boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
					}}
				>
					<h4
						style={{
							margin: "0 0 12px 0",
							color: "#2c3e50",
							fontSize: "16px",
							fontWeight: "600",
						}}
					>
						{title}
					</h4>
					{children}
				</div>
			);

			const SummaryRow = ({
				label,
				value,
			}: { label: string; value: string | React.ReactNode }) => (
				<div
					style={{
						display: "flex",
						marginBottom: "8px",
						paddingBottom: "8px",
						borderBottom: "1px solid #f1f3f4",
					}}
				>
					<span
						style={{
							fontWeight: "500",
							color: "#495057",
							minWidth: "140px",
							marginRight: "12px",
						}}
					>
						{label}:
					</span>
					<span style={{ color: "#6c757d", flex: 1 }}>
						{value || "Not provided"}
					</span>
				</div>
			);

			return (
				<div key={fieldName} style={{ position: "relative" }}>
					<Form.Item label={getFieldLabel(field)}>
						<div
							style={{
								backgroundColor: "#f8f9fa",
								padding: "20px",
								borderRadius: "8px",
								border: "1px solid #dee2e6",
							}}
						>
							{/* Location Information */}
							<SummaryCard title="📍 Location Information">
								<SummaryRow
									label="Store Location"
									value={
										locationDisplayText ||
										formValues.location_id ||
										"Not selected"
									}
								/>
							</SummaryCard>

							{/* Asset Details */}
							<SummaryCard title="🏭 Asset Details">
								<SummaryRow
									label="Asset Location"
									value={getDisplayLabel(
										"assetLocation",
										formValues.assetLocation,
									)}
								/>
								{hasRoomNumber && formValues.roomNumber && (
									<SummaryRow
										label="Room Number"
										value={formValues.roomNumber}
									/>
								)}
								<SummaryRow
									label="Asset Type"
									value={getDisplayLabel("assetType", formValues.assetType)}
								/>
								<SummaryRow
									label="Manufacturer"
									value={getDisplayLabel(
										"manufacturer",
										formValues.manufacturer,
									)}
								/>
								{formValues.qsrStore && (
									<SummaryRow label="QSR Store" value={formValues.qsrStore} />
								)}
								{hasStoreName && formValues.storeName && (
									<SummaryRow label="Store Name" value={formValues.storeName} />
								)}
								{hasStoreType && formValues.storeType && (
									<SummaryRow label="Store Type" value={formValues.storeType} />
								)}
								<SummaryRow
									label="Condition Rating"
									value={
										formValues.conditionRating ? (
											<span
												style={{
													color:
														formValues.conditionRating >= 4
															? "#28a745"
															: formValues.conditionRating >= 3
																? "#ffc107"
																: "#dc3545",
													fontWeight: "600",
												}}
											>
												{formValues.conditionRating}/5 ⭐
											</span>
										) : (
											"Not rated"
										)
									}
								/>
							</SummaryCard>

							{/* Manufacturer Plate Information */}
							<SummaryCard title="🏷️ Manufacturer Plate">
								<SummaryRow
									label="Has Plate"
									value={
										formValues.hasManufacturerPlate === "yes"
											? "✅ Yes"
											: formValues.hasManufacturerPlate === "no"
												? "❌ No"
												: "Not answered"
									}
								/>
								{formValues.hasManufacturerPlate === "yes" && (
									<>
										<SummaryRow
											label="Model Number"
											value={formValues.modelNumber}
										/>
										<SummaryRow
											label="Serial Number"
											value={formValues.serialNumber}
										/>
										<SummaryRow
											label="Manufacturer Date"
											value={
												formValues.manufacturerDate
													? typeof formValues.manufacturerDate === "string"
														? formValues.manufacturerDate
														: formValues.manufacturerDate.format?.("MM/DD/YYYY")
													: "Not provided"
											}
										/>

										{/* Refrigeration Details */}
										<SummaryRow
											label="Has Refrigeration Details"
											value={
												formValues.hasRefrigerationDetails === "yes"
													? "✅ Yes"
													: formValues.hasRefrigerationDetails === "no"
														? "❌ No"
														: "Not answered"
											}
										/>
										{formValues.hasRefrigerationDetails === "yes" && (
											<div
												style={{
													marginTop: "12px",
													paddingTop: "12px",
													borderTop: "1px solid #e9ecef",
												}}
											>
												<div
													style={{
														fontWeight: "600",
														marginBottom: "8px",
														color: "#495057",
													}}
												>
													Refrigeration Details:
												</div>
												{formValues.refrigerant && (
													<SummaryRow
														label="Refrigerant Type"
														value={formValues.refrigerant}
													/>
												)}
												{formValues.refrigerantCharge && (
													<SummaryRow
														label="Refrigerant Quantity"
														value={`${formValues.refrigerantCharge}${formValues.refrigerantUnit ? ` ${formValues.refrigerantUnit}` : ""}`}
													/>
												)}
											</div>
										)}

										{/* Warranty Information - only show if warranty fields exist in config */}
										{hasWarrantyFields &&
											(formValues.warrantyPeriod ||
												formValues.warrantyStartDate ||
												formValues.warrantyExpirationDate ||
												formValues.warrantyProvider) && (
												<div
													style={{
														marginTop: "12px",
														paddingTop: "12px",
														borderTop: "1px solid #e9ecef",
													}}
												>
													<div
														style={{
															fontWeight: "600",
															marginBottom: "8px",
															color: "#495057",
														}}
													>
														Warranty Information:
													</div>
													{formValues.warrantyPeriod && (
														<SummaryRow
															label="Warranty Period"
															value={`${formValues.warrantyPeriod} months`}
														/>
													)}
													{formValues.warrantyStartDate && (
														<SummaryRow
															label="Warranty Start"
															value={
																typeof formValues.warrantyStartDate === "string"
																	? formValues.warrantyStartDate
																	: formValues.warrantyStartDate.format?.(
																			"MM/DD/YYYY",
																		)
															}
														/>
													)}
													{formValues.warrantyExpirationDate && (
														<SummaryRow
															label="Warranty Expiration"
															value={
																typeof formValues.warrantyExpirationDate ===
																"string"
																	? formValues.warrantyExpirationDate
																	: formValues.warrantyExpirationDate.format?.(
																			"MM/DD/YYYY",
																		)
															}
														/>
													)}
													{formValues.warrantyProvider && (
														<SummaryRow
															label="Warranty Provider"
															value={formValues.warrantyProvider}
														/>
													)}
												</div>
											)}
									</>
								)}
							</SummaryCard>

							{/* Asset Tag Information */}
							<SummaryCard title="🏷️ Asset Tag">
								<SummaryRow
									label="Has Asset Tag"
									value={
										formValues.hasAssetTag === "yes"
											? "✅ Yes"
											: formValues.hasAssetTag === "no"
												? "❌ No"
												: "Not answered"
									}
								/>
								{formValues.hasAssetTag === "yes" && (
									<>
										<SummaryRow
											label="Asset Tag Number"
											value={formValues.assetTag}
										/>
										{hasArchivedAssetTag && formValues.archivedAssetTag && (
											<SummaryRow
												label="Previous Asset Tag"
												value={formValues.archivedAssetTag}
											/>
										)}
									</>
								)}
							</SummaryCard>

							{/* Photos */}
							<SummaryCard title="📸 Photos Captured">
								<div
									style={{
										display: "grid",
										gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
										gap: "12px",
									}}
								>
									{formValues.equipmentPhoto &&
										Array.isArray(formValues.equipmentPhoto) &&
										formValues.equipmentPhoto.length > 0 && (
											<div
												style={{
													padding: "8px",
													backgroundColor: "#e8f5e8",
													borderRadius: "6px",
													border: "1px solid #c3e6c3",
												}}
											>
												<span style={{ fontSize: "14px", fontWeight: "500" }}>
													✅ Equipment Photo
												</span>
											</div>
										)}
									{formValues.manufacturerPlatePhoto &&
										Array.isArray(formValues.manufacturerPlatePhoto) &&
										formValues.manufacturerPlatePhoto.length > 0 && (
											<div
												style={{
													padding: "8px",
													backgroundColor: "#e8f5e8",
													borderRadius: "6px",
													border: "1px solid #c3e6c3",
												}}
											>
												<span style={{ fontSize: "14px", fontWeight: "500" }}>
													✅ Manufacturer Plate Photo
												</span>
											</div>
										)}
									{formValues.assetTagPhoto &&
										Array.isArray(formValues.assetTagPhoto) &&
										formValues.assetTagPhoto.length > 0 && (
											<div
												style={{
													padding: "8px",
													backgroundColor: "#e8f5e8",
													borderRadius: "6px",
													border: "1px solid #c3e6c3",
												}}
											>
												<span style={{ fontSize: "14px", fontWeight: "500" }}>
													✅ Asset Tag Photo
												</span>
											</div>
										)}
								</div>
								{(!formValues.equipmentPhoto ||
									formValues.equipmentPhoto.length === 0) &&
									(!formValues.manufacturerPlatePhoto ||
										formValues.manufacturerPlatePhoto.length === 0) &&
									(!formValues.assetTagPhoto ||
										formValues.assetTagPhoto.length === 0) && (
										<div
											style={{
												padding: "12px",
												backgroundColor: "#fff3cd",
												borderRadius: "6px",
												border: "1px solid #ffeaa7",
												textAlign: "center",
											}}
										>
											<span style={{ color: "#856404" }}>
												⚠️ No photos captured yet
											</span>
										</div>
									)}
							</SummaryCard>
						</div>
					</Form.Item>
				</div>
			);
		}

		switch (field.type) {
			case "text":
			case "textarea":
			case "number": {
				if (field.type === "number") {
					return (
						<div key={fieldName} style={{ position: "relative" }}>
							{SkipButton}
							<Form.Item {...commonProps}>
								<InputNumber
									style={{ width: "100%" }}
									disabled={field.disabled || isLoading}
									placeholder={fieldPlaceholder}
									suffix={fieldSuffix}
									className={isLoading ? "loading-field" : ""}
								/>
							</Form.Item>
						</div>
					);
				}
				const Component = field.type === "text" ? Input : Input.TextArea;
				const handleChange = (e: any) => {
					if (formatFn) {
						const val = e?.target?.value;
						if (typeof val === "string") {
							const formatted = formatFn(val);
							const newData = { ...formData, [fieldName]: formatted };
							setFormData(newData);
							frm.setFieldsValue(newData);
						}
					}
				};
				const className =
					fieldName === "serialNumber" || fieldName === "modelNumber"
						? "submission-detail-enhanced"
						: "";
				return (
					<div key={fieldName} style={{ position: "relative" }}>
						{SkipButton}
						<Form.Item {...commonProps}>
							<Component
								disabled={field.disabled || isLoading}
								placeholder={fieldPlaceholder}
								className={`${className} ${isLoading ? "loading-field" : ""}`}
								suffix={fieldSuffix}
								onChange={formatFn ? handleChange : undefined}
								style={{ width: "100%" }}
							/>
						</Form.Item>
					</div>
				);
			}

			case "rating":
				return (
					<div key={fieldName} style={{ position: "relative" }}>
						{SkipButton}
						<Form.Item {...commonProps}>
							<RatingInput
								disabled={field.disabled || isLoading}
								count={field.options?.length || 5}
							/>
						</Form.Item>
					</div>
				);

			case "select": {
				const opts = fieldOptions[fieldName] || field.options || [];
				return (
					<div key={fieldName} style={{ position: "relative" }}>
						{SkipButton}
						<Form.Item {...commonProps}>
							<Select
								suffixIcon={fieldSuffix}
								disabled={field.disabled || isLoading}
								placeholder={fieldPlaceholder}
								showSearch
								autoClearSearchValue
								optionFilterProp="children"
								className={isLoading ? "loading-field" : ""}
							>
								{opts.map((op) => (
									<Select.Option key={op.value} value={op.value}>
										{op.label}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</div>
				);
			}

			case "date":
				return (
					<div key={fieldName} style={{ position: "relative" }}>
						{SkipButton}
						<Form.Item {...commonProps}>
							<DatePicker
								suffixIcon={fieldSuffix}
								format="MM/DD/YYYY"
								disabled={field.disabled || isLoading}
								placeholder={fieldPlaceholder}
								style={{ width: "100%" }}
								className={isLoading ? "loading-field" : ""}
							/>
						</Form.Item>
					</div>
				);

			case "radio": {
				const radioOpts = fieldOptions[fieldName] || field.options || [];
				return (
					<div key={fieldName} style={{ position: "relative" }}>
						{SkipButton}
						<Form.Item {...commonProps}>
							<Radio.Group disabled={field.disabled || isLoading}>
								{radioOpts.map((op) => (
									<Radio key={op.value} value={op.value}>
										{op.label}
									</Radio>
								))}
							</Radio.Group>
						</Form.Item>
					</div>
				);
			}

			case "checkbox":
				return (
					<div key={fieldName} style={{ position: "relative" }}>
						{SkipButton}
						<Form.Item {...commonProps} valuePropName="checked">
							<Input
								type="checkbox"
								disabled={field.disabled || isLoading}
								suffix={fieldSuffix}
							/>
						</Form.Item>
					</div>
				);

			case "location":
				return (
					<div key={fieldName} style={{ position: "relative" }}>
						{SkipButton}
						<Form.Item {...commonProps}>
							<DynamicLocationPicker
								value={frm.getFieldValue(fieldName)}
								onChange={(val) => {
									const newData = { ...formData, [fieldName]: val };
									setFormData(newData);
									frm.setFieldsValue(newData);
								}}
								placeholder={fieldPlaceholder}
								disabled={field.disabled || isLoading}
							/>
						</Form.Item>
					</div>
				);

			case "image":
			case "file": {
				const maxCount = field?.draggerProps?.maxCount || 1;
				const isDisabled = field.disabled || isLoading;

				return (
					<div key={fieldName} style={{ position: "relative" }}>
						{SkipButton}
						<Form.Item
							{...commonProps}
							valuePropName="fileList"
							getValueFromEvent={normFile}
						>
							<Dragger
								name={field.name}
								listType="picture-card"
								multiple={field?.draggerProps?.multiple || false}
								accept={field?.draggerProps?.accept || "*"}
								showUploadList={{
									showRemoveIcon: true,
									showPreviewIcon: true,
								}}
								disabled={isDisabled}
								maxCount={maxCount}
								beforeUpload={() => {
									const fileList = form.getFieldValue(fieldName) || [];
									if (fileList.length >= maxCount) {
										message.warning(`Max file count of ${maxCount} reached`);
										return Upload.LIST_IGNORE;
									}
									return true;
								}}
								customRequest={async (opts) => {
									const { file: rawFile, onSuccess, onError } = opts;
									try {
										// Show loading message if HEIC conversion is needed
										const file = rawFile as File;
										const wasHeic = isHeicFile(file);

										if (wasHeic) {
											message.loading({
												content: "Converting HEIC image...",
												key: "convert-form-image",
												duration: 0,
											});
										}

										// Process the file (convert HEIC to JPEG if needed)
										const processedFile = await processImageFile(file);

										// Destroy loading message if it was shown
										if (wasHeic) {
											message.destroy("convert-form-image");
											message.success("Image converted successfully");
										}

										const dataUrl = (await getBase64(processedFile)) as string;
										const resp = await uploadFieldCaptureFromDataUrl(dataUrl);
										const finalUrl =
											typeof resp === "string" ? resp : (resp as any)?.url;

										// For HEIC files, also pass the preview URL since the original file can't be previewed
										onSuccess?.({
											url: finalUrl,
											...(wasHeic && { thumbUrl: dataUrl }),
										});
									} catch (error) {
										console.error("Error uploading file:", error);
										message.destroy("convert-form-image");
										onError?.(error as Error);
									}
								}}
								onChange={async (info) => {
									const { fileList, file } = info;

									// For HEIC files, ensure the thumbUrl from response is set on the file for preview
									const updatedFileList = fileList.map((f) => {
										if (f.uid === file.uid && f.response?.thumbUrl) {
											return { ...f, thumbUrl: f.response.thumbUrl };
										}
										return f;
									});

									frm.setFieldsValue({ [fieldName]: updatedFileList });

									if (file.status === "done") {
										message.success(`${file.name} uploaded successfully`);
										const fileUrl = file.response?.url || file.url || "";

										const pluginName = field.metadata?.pluginName as string;
										if (pluginName && dfsPluginRegistry[pluginName]) {
											// Possibly process via plugin
											const propMap =
												(field.metadata?.propMap as Record<string, string>) ||
												{};
											const targetFields = Object.values(propMap);
											// Mark them loading
											setPluginEnhancements((prev) => {
												const next = { ...prev };
												targetFields.forEach((tf) => {
													next[tf] = { ...prev[tf], loading: true };
												});
												return next;
											});

											try {
												const pluginFn: DFSPluginFn =
													dfsPluginRegistry[pluginName];
												const pluginDependencies =
													field.metadata?.pluginDependencies || {};
												const optionsFieldName = (pluginDependencies as any)
													.options;
												const theFieldOptions = optionsFieldName
													? state.fieldOptions[optionsFieldName]
													: undefined;

												const snapshot = useFormStore.getState().formData;
												const newFormDataWithFiles = {
													...snapshot,
													[fieldName]: fileList,
												};

												const { updatedFormData, uiPatches } = await pluginFn(
													field,
													newFormDataWithFiles,
													{
														fileUrl,
														formSections: formConfig?.config.sections,
														dependencies: pluginDependencies,
														fieldOptions: {
															[optionsFieldName]: theFieldOptions,
														},
													},
												);

												const latest = useFormStore.getState().formData;
												const changedKeys = Object.keys(updatedFormData).filter(
													(k) => updatedFormData[k] !== latest[k],
												);
												const merged = { ...latest };
												changedKeys.forEach((ck) => {
													merged[ck] = updatedFormData[ck];
												});
												merged[fieldName] = fileList;

												setFormData(merged);
												frm.setFieldsValue(merged);

												setPluginEnhancements((prev) => {
													const nextState = { ...prev };
													targetFields.forEach((tf) => {
														nextState[tf] = {
															...prev[tf],
															...uiPatches[tf],
															loading: false,
														};
													});
													return nextState;
												});
											} catch (error) {
												setPluginEnhancements((prev) => {
													const next = { ...prev };
													targetFields.forEach((tf) => {
														next[tf] = {
															...prev[tf],
															loading: false,
															validateStatus: "error",
															help: "Failed to process image",
														};
													});
													return next;
												});
												console.error("Plugin error:", error);
											}
										}
									} else if (file.status === "error") {
										message.error(`${file.name} upload failed.`);
									}
								}}
								onPreview={handlePreview}
							>
								<p className="ant-upload-drag-icon">
									<InboxOutlined />
								</p>
								<p className="ant-upload-text">Click or drag file to upload</p>
								{field?.draggerProps?.multiple && (
									<span className="ant-upload-hint">
										Multiple files supported ({maxCount} max)
									</span>
								)}
							</Dragger>
						</Form.Item>
					</div>
				);
			}

			default:
				return null;
		}
	}

	/**
	 * Steps definition
	 *
	 * Generates step configuration from form sections, filtering out sections with no visible fields.
	 * This ensures that the "Additional Information" section only appears when custom fields are visible.
	 */
	const stepsConfig = useMemo(() => {
		if (!formConfig) return [];
		return formConfig.config.sections
			.map((sec, secIndex) => {
				const fieldNames = getFieldNames(sec.fields || sec.questions);
				const content = (
					<Row gutter={[16, 16]}>
						{(sec.fields || sec.questions)?.map((fld, idx) => {
							const nm =
								fld.name || fld.responseTag || fld.key || `field-${idx}`;
							return (
								<Col
									xs={24}
									sm={24}
									md={24}
									lg={24}
									xl={24}
									key={`field-${nm}`}
								>
									{renderField(fld, form)}
								</Col>
							);
						})}
					</Row>
				);
				return {
					title: sec.title,
					fieldNames,
					content,
					sectionIndex: secIndex,
				};
			})
			.filter((step) => {
				// Filter out sections that have no visible fields (e.g., "Additional Information" with no fields)
				const section = formConfig.config.sections[step.sectionIndex];
				if (!section) return false;
				const fields = section.fields || section.questions || [];
				// Count how many fields would actually render (not hidden by conditionals)
				const visibleFieldsCount = fields.filter((field) => {
					const fieldName = field.name || field.responseTag || field.key;
					if (!fieldName) return false;
					return evaluateCondition(field, form, state.fieldOptions, formConfig);
				}).length;
				return visibleFieldsCount > 0;
			})
			.map(({ sectionIndex, ...step }) => step); // Remove sectionIndex from final result
	}, [
		formConfig,
		formData,
		form,
		pluginEnhancements,
		fieldOptions,
		assetLocation,
	]);

	/** Move to specific step (index) */
	async function handleStepChange(idx: number) {
		if (idx < 0 || idx >= stepsConfig.length) return;
		// If user is going forward, we validate current step
		if (idx > state.currentStep) {
			await attemptStepNavigation(idx);
		} else {
			dispatch({ type: "SET_CURRENT_STEP", payload: idx });
			window.scrollTo({ top: 0, behavior: "smooth" });
		}
	}
	async function validateCurrentStep() {
		try {
			const fields = stepsConfig[state.currentStep]?.fieldNames || [];
			await form.validateFields(fields);
			return true;
		} catch {
			return false;
		}
	}

	async function next() {
		await attemptStepNavigation(state.currentStep + 1);
	}
	function prev() {
		if (state.currentStep > 0) {
			dispatch({ type: "SET_CURRENT_STEP", payload: state.currentStep - 1 });
			window.scrollTo({ top: 0, behavior: "smooth" });
		}
	}

	/**
	 * Merges submission data values into form configuration fields
	 *
	 * This function handles the mismatch between original database submissions and restructured
	 * form configurations (especially for Form ID 3). Instead of matching sections by index position,
	 * it searches for fields by name across all sections, allowing data to be properly merged even
	 * when the form structure has been reorganized.
	 *
	 * @param configSections - The restructured form configuration sections
	 * @param dataSections - The original submission data sections
	 */
	function mergeSubmissionIntoConfig(
		configSections: DFSFormSection[],
		dataSections: any[], // e.g. dataSections[i] = { fields: [...] }
	) {
		// Collect ALL data fields from ALL sections to enable flexible field matching
		// This is crucial for Form ID 3 where manufacturer plate fields may have moved
		// from "Asset Details" section to "Manufacturer Plate" section during restructuring
		const allDataFields: any[] = [];
		dataSections.forEach((dataSection) => {
			const dataFields = dataSection?.fields || dataSection?.questions || [];
			allDataFields.push(...dataFields);
		});

		// Process each config section and merge matching fields by name
		for (const cfgSection of configSections) {
			const cfgFields = cfgSection.fields || cfgSection.questions || [];

			// For each field in the config, search across ALL data fields (not just same section)
			for (const cfgField of cfgFields) {
				const matchingDataField = allDataFields.find(
					(df: any) => df.name === cfgField.name,
				);

				// Skip if no matching field found in the original data
				if (!matchingDataField) {
					continue;
				}

				// Merge the field value from original data into the config field
				if (matchingDataField.value !== undefined) {
					cfgField.value = matchingDataField.value;
				}

				// Preserve any disabled state from the original submission
				if (typeof matchingDataField.disabled === "boolean") {
					cfgField.disabled = matchingDataField.disabled;
				}

				// Handle nested fields (groups and dynamic lists) recursively
				if (cfgField.fields && matchingDataField.fields) {
					mergeSubmissionIntoConfig(
						// Create temporary section structure for recursion
						[{ title: cfgField.name || "", fields: cfgField.fields }],
						[{ fields: matchingDataField.fields }],
					);
				}
			}
		}
	}

	/** Check which required fields are missing (not disabled) on the given step. */
	function gatherMissingRequiredFieldsForSection(stepIndex: number): string[] {
		if (!formConfig || !stepsConfig[stepIndex]) return [];
		const missing: string[] = [];
		const section = formConfig.config.sections[stepIndex];
		const fields = section.fields || section.questions || [];

		function checkField(field: DFSFormField, prefix = "") {
			const fieldName = field.name || field.responseTag || field.key;
			if (!fieldName) return;
			const fullName = prefix ? `${prefix}.${fieldName}` : fieldName;

			// ignore disabled
			if (field.disabled) return;

			// if group => recurse
			if (field.type === "group" && field.fields) {
				field.fields.forEach((sf) => checkField(sf, fullName));
				return;
			}

			// if dynamicList => iterate each item
			if (field.type === "dynamicList" && field.fields) {
				const count = getDynamicListCount(form.getFieldsValue(true), field);
				for (let i = 0; i < count; i++) {
					field.fields.forEach((sf) => checkField(sf, `${fullName}.${i}`));
				}
				return;
			}

			// if hidden by conditional => skip
			if (!evaluateCondition(field, form, state.fieldOptions, formConfig))
				return;

			// check if required
			const validations = Array.isArray(field.validation)
				? field.validation
				: field.validation
					? [field.validation]
					: [];
			const isRequired = validations.some((v) => v.required);
			if (!isRequired) return;

			const val = form.getFieldValue(fullName);
			if (isValueEmpty(val, field.type)) missing.push(fullName);
		}

		fields.forEach((f) => checkField(f));
		return missing;
	}

	/**
	 * Attempt to navigate to a target step, highlighting missing required fields if any.
	 */
	async function attemptStepNavigation(targetStep: number) {
		const missingRequired = gatherMissingRequiredFieldsForSection(
			state.currentStep,
		);

		if (missingRequired.length > 0) {
			// Highlight missing required fields in red
			setHighlightedFields(new Set(missingRequired));
			message.error(
				"Please fill in all required fields or skip them before proceeding.",
			);
			return;
		}

		// Clear any previous highlights
		setHighlightedFields(new Set());

		// If no missing required, do a normal antd validation
		try {
			await validateCurrentStep(); // form.validateFields(...) for the current step

			// Always save draft before navigating to next step (regardless of auto_save setting)
			if (targetStep > state.currentStep && !draftDisabled) {
				try {
					const formIdFromState =
						(location.state?.formId as string) || id || null;
					if (formIdFromState) {
						// Convert organization-specific form ID to global form ID (3 or 7)
						const orgFormIdNum = Number.parseInt(formIdFromState, 10);
						const orgForm = user?.organization?.forms?.find(
							(f: any) =>
								f.id === orgFormIdNum ||
								f.formId === orgFormIdNum ||
								f.form_id === orgFormIdNum,
						);

						// Get the global form ID (2, 3, 4, 7, etc.)
						const globalFormId = orgForm?.formId || orgForm?.form_id;
						const globalFormIdNum =
							typeof globalFormId === "number"
								? globalFormId
								: globalFormId
									? Number.parseInt(String(globalFormId), 10)
									: null;

						// Valid form IDs: 2 (Handyman Checklist), 3 (Inventory & Tagging), 4 (QC Checklist), 7 (Property Condition Assessment)
						const validFormIds = [2, 3, 4, 7];
						if (globalFormIdNum && validFormIds.includes(globalFormIdNum)) {
							const allValues = form.getFieldsValue();
							// Check multiple sources for location_id: watched form values, form values, formData state, and location.state
							// Handle both number and object formats (location field might be {id: number})
							let locId =
								watchedLocationId ||
								watchedLocation ||
								allValues.location_id ||
								allValues.location ||
								formData.location_id ||
								formData.location ||
								location.state?.locationId;

							// If locId is an object, extract the id property
							if (locId && typeof locId === "object" && "id" in locId) {
								locId = locId.id;
							}

							if (locId) {
								const dataToSave = buildSubmissionData(formConfig, {
									...formData,
									...allValues,
								});
								if (dataToSave) {
									// Use Core API endpoint which handles both create and update automatically
									// It finds existing drafts based on form_id, location_id, and user_id
									try {
										const savedDraftId = await createDraft({
											location_id: locId,
											form_id: globalFormIdNum,
											data: dataToSave,
										});

										// Always update draftId to ensure it's set for subsequent saves
										if (savedDraftId) {
											setDraftId(savedDraftId);
										}
									} catch (draftError) {
										console.error("Draft save error:", draftError);
										// Don't throw - let navigation continue even if draft save fails
									}
								} else {
									console.warn(
										"No data to save - buildSubmissionData returned null/undefined",
									);
								}
							} else {
								console.warn(
									"No location ID found for draft save. Available values:",
									{
										watchedLocationId,
										watchedLocation,
										allValues_location_id: allValues.location_id,
										allValues_location: allValues.location,
										formData_location_id: formData.location_id,
										formData_location: formData.location,
										location_state_locationId: location.state?.locationId,
									},
								);
							}
						} else {
							console.warn(
								"Invalid global form ID for draft save. Expected one of [2, 3, 4, 7], but found:",
								globalFormIdNum,
							);
						}
					} else {
						console.warn("No form ID found for draft save");
					}
				} catch (error) {
					// Log error but don't block navigation
					console.error("Failed to save draft on step navigation:", error);
				}
			}

		dispatch({ type: "SET_CURRENT_STEP", payload: targetStep });
			window.scrollTo({ top: 0, behavior: "smooth" });
	} catch {
		// If any other validation errors, remain on this step
		message.error("Please fix validation errors before proceeding.");
	}
}

	/**
	 * Toggle the skip state of an individual field
	 */
	function toggleFieldSkip(fieldName: string) {
		if (!formConfig) return;

		// Create a deep copy of the config to modify
		const updatedConfig = { ...formConfig };
		updatedConfig.config = { ...formConfig.config };
		updatedConfig.config.sections = [...formConfig.config.sections];

		// Find and toggle the field's disabled state
		let fieldFound = false;
		for (const section of updatedConfig.config.sections) {
			const fields = section.fields || section.questions || [];
			fieldFound = toggleFieldInArray(fields, fieldName);
			if (fieldFound) break;
		}

		if (fieldFound) {
			setFormConfig(updatedConfig);
			// Remove from highlighted fields if it was highlighted
			setHighlightedFields((prev) => {
				const newSet = new Set(prev);
				newSet.delete(fieldName);
				return newSet;
			});
		}
	}

	function toggleFieldInArray(
		fields: DFSFormField[],
		targetFieldName: string,
	): boolean {
		for (const field of fields) {
			const fieldName = field.name || field.responseTag || field.key;
			if (fieldName === targetFieldName) {
				field.disabled = !field.disabled;
				return true;
			}
			if (field.fields && field.fields.length > 0) {
				if (toggleFieldInArray(field.fields, targetFieldName)) {
					return true;
				}
			}
		}
		return false;
	}

	// Note: handleManageSkippedClick function was removed as it was unused

	/** Which fields in this section are currently disabled (skipped)? */
	function getSkippedFieldsInSection(stepIndex: number): string[] {
		if (!formConfig) return [];
		const section = formConfig.config.sections[stepIndex];
		const fields = section.fields || section.questions || [];
		const skipped: string[] = [];

		function checkField(field: DFSFormField, prefix = "") {
			const fieldName = field.name || field.responseTag || field.key;
			if (!fieldName) return;
			const fullName = prefix ? `${prefix}.${fieldName}` : fieldName;

			// If the config says this field is disabled => it's "skipped."
			if (field.disabled) {
				skipped.push(fullName);
			}

			// Recurse if group or dynamicList
			if (field.type === "group" && field.fields) {
				field.fields.forEach((sf) => checkField(sf, fullName));
			} else if (field.type === "dynamicList" && field.fields) {
				const count = getDynamicListCount(form.getFieldsValue(true), field);
				for (let i = 0; i < count; i++) {
					field.fields.forEach((sf) => checkField(sf, `${fullName}.${i}`));
				}
			}
		}

		fields.forEach((f) => checkField(f));
		return skipped;
	}

	/** Recompute dynamicList counts whenever form changes */
	function updateAllDynamicListCounts(allVals: Record<string, any>) {
		if (!formConfig) return;
		const newState: Record<string, number> = {};
		const allFs = getAllFields(formConfig.config.sections);
		allFs.forEach((f) => {
			if (f.type === "dynamicList" && f.name) {
				const c = getDynamicListCount(allVals, f);
				newState[f.name] = c;
			}
		});
		if (Object.keys(newState).length > 0) {
			dispatch({
				type: "SET_DYNAMIC_LIST_STATE",
				payload: newState,
			});
		}
	}

	/** On form value change => compute fields + auto-save + dataSource deps */
	const onFormValuesChange = useCallback(
		(
			changedValues: Record<string, unknown>,
			allValues: Record<string, unknown>,
		) => {
			if (!formConfig) return;
			let mergedData = { ...formData, ...allValues };

			// Special handling for Form ID 3: Auto-populate defaults when hasManufacturerPlate changes
			const formIdFromState = (location.state?.formId as string) || id || null;
			if (
				formIdFromState === "3" &&
				changedValues.hasManufacturerPlate !== undefined
			) {
				if (changedValues.hasManufacturerPlate === "no") {
					// Auto-populate manufacturer with default "MANUFACTURER NOT LISTED" value
					// First, check if we have manufacturer options loaded to find the correct ID
					const manufacturerOptions = fieldOptions.manufacturer || [];

					// Look for "MANUFACTURER NOT LISTED" option (it should have ID 2719 based on user request)
					let defaultManufacturerId: string | number = 2719; // Default to 2719 as requested
					const manufacturerNotListedOption = manufacturerOptions.find(
						(opt: any) =>
							opt.label &&
							opt.label.toUpperCase().includes("MANUFACTURER NOT LISTED"),
					);
					if (manufacturerNotListedOption) {
						defaultManufacturerId = manufacturerNotListedOption.value;
					}

					// Set default values for manufacturer and model number when no plate is present
					mergedData = {
						...mergedData,
						manufacturer: defaultManufacturerId,
						modelNumber: "0", // Default model number as requested
					};

					// Update the form fields immediately to reflect these defaults
					form.setFieldsValue(mergedData);
				} else if (changedValues.hasManufacturerPlate === "yes") {
					// Clear the default values when user switches back to "yes"
					// Only clear if they currently have the default values
					if (
						mergedData.manufacturer === 2719 &&
						mergedData.modelNumber === "0"
					) {
						mergedData = {
							...mergedData,
							manufacturer: undefined,
							modelNumber: undefined,
						};

						// Update the form fields immediately to reflect the cleared values
						form.setFieldsValue(mergedData);
					}
				}
			}

			const computedData = computeAllFields(mergedData, formConfig);

			if (computedData !== mergedData) {
				form.setFieldsValue(computedData);
				setFormData(computedData);
				attemptAutoSave(computedData);
			} else {
				setFormData(mergedData);
				attemptAutoSave(mergedData);
			}

			updateAllDynamicListCounts(computedData);

			const changedKeys: string[] = Object.keys(changedValues);
			const allFs = getAllFields(formConfig.config.sections);
			allFs.forEach((f) => {
				// If a parentField changed, refetch
				if (
					f.dataSource?.metadata?.parentField &&
					changedKeys.includes(
						(f.dataSource.metadata as any).parentField as string,
					)
				) {
					fetchFieldData(f, allValues);
				}
				// If dependencies changed, refetch
				if (
					f.dataSource?.dependencies?.some((dep) => changedKeys.includes(dep))
				) {
					fetchFieldData(f, computedData);
				}
			});
		},
		[
			formConfig,
			formData,
			form,
			fetchFieldData,
			fieldOptions,
			location.state?.formId,
			id,
		],
	);

	async function onFinish(submitType: "submit" | "submitAndNew" = "submit") {
		try {
			// Cancel any pending auto-saves and disable drafts
			saveProgressDebounced.cancel();
			setDraftDisabled(true);
			// Check for missing required fields on the last step
			const lastStepIndex = stepsConfig.length - 1;
			const missingRequired =
				gatherMissingRequiredFieldsForSection(lastStepIndex);

			// If fields are missing, highlight them and show error message
			if (missingRequired.length > 0) {
				setHighlightedFields(new Set(missingRequired));
				message.error(
					"Please fill in all required fields or skip them before submitting.",
				);
				setDraftDisabled(false); // Re-enable drafts
				return;
			}

			// Clear any highlights and proceed with submission
			setHighlightedFields(new Set());

			// If no missing required fields, proceed:
			if (submitType === "submitAndNew") {
				await finalSubmitAndStartNew();
			} else {
				await finalSubmit();
			}
		} catch (err: any) {
			// Re-enable drafts if something went wrong
			setDraftDisabled(false);
			// Extract error message from the error object
			const errorMessage =
				err instanceof Error
					? err.message
					: typeof err === "string"
						? err
						: "Please fix all validation errors.";
			message.error(errorMessage);
		}
	}

	async function submitFormData() {
		const locId =
			formData.location_id || formData.location || location.state?.locationId;
		const formIdFromState = (location.state?.formId as string) || id || null;

		if (!formIdFromState || !locId) {
			message.error(
				!formIdFromState ? "No formId provided." : "No location_id provided.",
			);
			return null;
		}

		// Convert organization-specific form ID to global form ID (3 or 7)
		// formIdFromState is the organization-specific form ID (e.g., 52)
		// We need to find the global form ID (3 or 7) from the organization form
		const orgFormIdNum = Number.parseInt(formIdFromState, 10);
		const orgForm = user?.organization?.forms?.find(
			(f: any) =>
				f.id === orgFormIdNum ||
				f.formId === orgFormIdNum ||
				f.form_id === orgFormIdNum,
		);

		// Get the global form ID (2, 3, 4, 7, etc.)
		const globalFormId = orgForm?.formId || orgForm?.form_id;
		const globalFormIdNum =
			typeof globalFormId === "number"
				? globalFormId
				: globalFormId
					? Number.parseInt(String(globalFormId), 10)
					: null;

		// Validate that we have a valid global form ID
		// Valid form IDs: 2 (Handyman Checklist), 3 (Inventory & Tagging), 4 (QC Checklist), 7 (Property Condition Assessment)
		const validFormIds = [2, 3, 4, 7];
		if (!globalFormIdNum || !validFormIds.includes(globalFormIdNum)) {
			message.error(
				`Invalid form configuration. Global form ID must be one of [${validFormIds.join(", ")}], but found: ${globalFormIdNum || "unknown"}. Please contact support.`,
			);
			return null;
		}

		const formDataWithLocation = {
			...formData,
			location_id: locId,
			location: locId,
		};
		const submissionData = buildSubmissionData(
			formConfig,
			formDataWithLocation,
		);
		if (!submissionData) {
			message.error("Could not build submission data.");
			return null;
		}

		let submissionId =
			draftId || location.state?.draftId || location.state?.submissionId;

		if (isCompleted && submissionId) {
			await updateSubmission(submissionId!, submissionData, locId);
			message.success("Form updated successfully.");
		} else {
			if (!submissionId) {
				submissionId = await createDraft({
					location_id: locId,
					form_id: globalFormIdNum, // Use global form ID (3 or 7)
					data: submissionData,
				});
			} else {
				await updateDraft({
					draft_id: submissionId,
					data: submissionData,
					form_id: globalFormIdNum, // Use global form ID (3 or 7)
					location_id: locId, // Required for draft update
				});
			}
			await submitSubmission(submissionId, submissionData);
			message.success("Form submitted successfully.");
		}

		return {
			submissionId,
			formIdFromState,
			locId,
			globalFormId: globalFormIdNum,
		};
	}

	async function finalSubmit(): Promise<boolean> {
		dispatch({ type: "SET_SUBMIT_STATE", payload: { isSubmitting: true } });

		try {
			const result = await submitFormData();
			if (!result) return false;

			const { submissionId, formIdFromState, locId } = result;

			// Cancel any pending auto-saves and clear state
			saveProgressDebounced.cancel();
			clearState();

			// Navigate to submission detail page
			const navPath = `/locations/${locId}/submissions/${submissionId}`;
			navigate(navPath, {
				state: {
					submissionId,
					formId: formIdFromState,
					...location.state,
				},
			});
			return true;
		} catch (err) {
			console.error("Submission error:", err);
			// Extract error message from the error object
			const errorMessage =
				err instanceof Error
					? err.message
					: typeof err === "string"
						? err
						: "Submission failed. Please fix errors and try again.";
			message.error(errorMessage);
			return false;
		} finally {
			dispatch({ type: "SET_SUBMIT_STATE", payload: { isSubmitting: false } });
		}
	}

	async function finalSubmitAndStartNew() {
		dispatch({ type: "SET_SUBMIT_STATE", payload: { isSubmittingNew: true } });

		try {
			const result = await submitFormData();
			if (!result) return;

			const { formIdFromState } = result;

			// Capture location data before clearing
			const locationId =
				form.getFieldValue("location_id") || form.getFieldValue("location");

			// Cancel any pending auto-saves
			saveProgressDebounced.cancel();
			// Reset state management
			dispatch({ type: "RESET_FORM_STATE" });
			// Reset form
			form.resetFields();
			// Clear form state
			clearState();

			// Navigate to a clean URL to prevent overwriting previous submission
			navigate(location.pathname, {
				replace: true,
				state: {
					formId: formIdFromState,
					locationId: locationId,
					isSubmissionCompleted: false,
				},
			});

			// Now follow the standard form initialization flow from useEffect
			// formIdFromState is the organization-specific form ID (e.g., 58)
			// We need to find the template form ID (3 or 7) from the organization form
			const orgFormIdNum = Number.parseInt(formIdFromState, 10);

			// Find the organization form to get the template form ID
			const orgForm = user?.organization?.forms?.find(
				(f: any) =>
					f.id === orgFormIdNum ||
					f.formId === orgFormIdNum ||
					f.form_id === orgFormIdNum,
			);

			if (!orgForm) {
				setInternalDataLoaded(true);
				return;
			}

			// Get the template form ID (3, 7, etc.)
			const templateFormId = orgForm?.formId || orgForm?.form_id;
			const templateFormIdNum =
				typeof templateFormId === "number"
					? templateFormId
					: templateFormId
						? Number.parseInt(String(templateFormId), 10)
						: 0;

			if (!templateFormIdNum || templateFormIdNum === 0) {
				setInternalDataLoaded(true);
				return;
			}

			// For template form ID 3, use hardcoded form config (not fetched from anywhere)
			if (templateFormIdNum === 3) {
				// Get logo from user store
				const orgLogo = user?.organization?.config?.logo_url || "";

				// Get custom fields from organization form config
				// orgForm.config can be either an array of fields directly or an object with uniqueFields property
				let customFields: Array<{
					name: string;
					label: string;
					type: string;
					required?: boolean;
					placeholder?: string;
					options?: string[];
					conditional?: {
						dependsOn: string;
						operator?: string;
						value?: unknown;
						values?: unknown[];
					};
				}> | null = null;

				if (Array.isArray(orgForm?.config)) {
					// Case 1: config is an array of fields directly
					customFields = orgForm.config as Array<{
						name: string;
						label: string;
						type: string;
						required?: boolean;
						placeholder?: string;
						options?: string[];
						conditional?: {
							dependsOn: string;
							operator?: string;
							value?: unknown;
							values?: unknown[];
						};
					}>;
				} else if (
					orgForm?.config &&
					typeof orgForm.config === "object" &&
					Array.isArray(orgForm.config.uniqueFields)
				) {
					// Case 2: config is an object with uniqueFields property
					customFields = orgForm.config.uniqueFields as Array<{
						name: string;
						label: string;
						type: string;
						required?: boolean;
						placeholder?: string;
						options?: string[];
						conditional?: {
							dependsOn: string;
							operator?: string;
							value?: unknown;
							values?: unknown[];
						};
					}>;
				}

				// Use hardcoded form config for form ID 3 (without custom fields yet)
				const baseConfig = getFormId3Config(orgLogo);

				setFormName(baseConfig.config.formName || "Inventory & Tagging");
				setFormLogo(baseConfig.config.logo || "");

				// Restructure form config for form ID 3 (this creates the hardcoded 5-page structure)
				const configForNew = restructureFormConfig(baseConfig);

				// Standard preprocessing
				preprocessSections(configForNew);
				prepareComputedFields(configForNew);

				// Append custom fields AFTER restructuring (so they're preserved)
				// This ensures custom fields appear before the Summary section
				const configForNewWithCustomFields = appendCustomFieldsToFormConfig(
					configForNew,
					customFields,
				);

				// Build initialValues from the form config
				const initialValues = buildInitialValuesFromFormConfig(
					configForNewWithCustomFields,
				);

				// Add our preserved location data
				initialValues.location_id = locationId;
				initialValues.location = locationId;

				// For form ID 3, ensure conditional fields are set to defaults for new submissions
				if (initialValues.hasManufacturerPlate === undefined) {
					initialValues.hasManufacturerPlate = "no";
				}
				if (initialValues.hasAssetTag === undefined) {
					initialValues.hasAssetTag = "no";
				}
				if (initialValues.hasRefrigerationDetails === undefined) {
					initialValues.hasRefrigerationDetails = "no";
				}

				// Auto-populate manufacturer and model defaults when no manufacturer plate
				if (initialValues.hasManufacturerPlate === "no") {
					if (!initialValues.manufacturer) {
						initialValues.manufacturer = 2719; // Default to "2719 - MANUFACTURER NOT LISTED"
					}
					if (!initialValues.modelNumber) {
						initialValues.modelNumber = "0"; // Default model number
					}
				}

				// Set the data in both state and form
				setFormData(initialValues);
				form.setFieldsValue(initialValues);
				setFormConfig(configForNewWithCustomFields);

				// Fetch any data sources (standard form initialization)
				const allFs = getAllFields(
					configForNewWithCustomFields.config.sections,
				);
				const promises = allFs
					.filter(
						(f) =>
							f.dataSource?.type === "api" ||
							(f.options && Array.isArray(f.options)),
					)
					.map((f) => fetchFieldData(f, initialValues));
				await Promise.all(promises);

				// Re-enable features
				setInternalDataLoaded(true);
				setShouldAutoSave(
					user?.organization?.config?.auto_save_drafts ?? false,
				);
				setIsCompleted(false);
				setPluginEnhancements({});
				setDraftId(undefined);
				setDraftDisabled(false);

				message.success("Started new form with preserved location.");
				return;
			}

			// For template form ID 7 or other forms, fetch from API using template form ID
			// getFormData already handles form ID 3 correctly (returns "Inventory & Tagging")
			await getFormData(
				user?.organization?.external_firebase_id!,
				templateFormIdNum,
				setFormName,
				setFormLogo,
				async (dbConfig: any) => {
					if (!dbConfig) {
						setFormConfig({ config: { sections: [] } });
						setInternalDataLoaded(true);
						return;
					}

					// Only restructure for form ID 3, otherwise use original config
					const configForNew =
						templateFormIdNum === 3
							? restructureFormConfig(dbConfig)
							: dbConfig;

					// Standard preprocessing
					preprocessSections(configForNew);
					prepareComputedFields(configForNew);

					// Append custom fields from organization form config if they exist
					// Only for form ID 3 - get uniqueFields from the org form
					let customFields: Array<{
						name: string;
						label: string;
						type: string;
						required?: boolean;
						placeholder?: string;
						options?: string[];
						conditional?: {
							dependsOn: string;
							operator?: string;
							value?: unknown;
							values?: unknown[];
						};
					}> | null = null;

					if (templateFormIdNum === 3) {
						if (Array.isArray(orgForm?.config)) {
							// Case 1: config is an array of fields directly
							customFields = orgForm.config as Array<{
								name: string;
								label: string;
								type: string;
								required?: boolean;
								placeholder?: string;
								options?: string[];
								conditional?: {
									dependsOn: string;
									operator?: string;
									value?: unknown;
									values?: unknown[];
								};
							}>;
						} else if (
							orgForm?.config &&
							typeof orgForm.config === "object" &&
							Array.isArray(orgForm.config.uniqueFields)
						) {
							// Case 2: config is an object with uniqueFields property
							customFields = orgForm.config.uniqueFields as Array<{
								name: string;
								label: string;
								type: string;
								required?: boolean;
								placeholder?: string;
								options?: string[];
								conditional?: {
									dependsOn: string;
									operator?: string;
									value?: unknown;
									values?: unknown[];
								};
							}>;
						}
					}
					const configForNewWithCustomFields = appendCustomFieldsToFormConfig(
						configForNew,
						customFields,
					);

					// Build initialValues from the form config
					const initialValues = buildInitialValuesFromFormConfig(
						configForNewWithCustomFields,
					);

					// Add our preserved location data
					initialValues.location_id = locationId;
					initialValues.location = locationId;

					// For form ID 3, ensure conditional fields are set to defaults for new submissions
					if (templateFormIdNum === 3) {
						if (initialValues.hasManufacturerPlate === undefined) {
							initialValues.hasManufacturerPlate = "no";
						}
						if (initialValues.hasAssetTag === undefined) {
							initialValues.hasAssetTag = "no";
						}
						if (initialValues.hasRefrigerationDetails === undefined) {
							initialValues.hasRefrigerationDetails = "no";
						}

						// Don't auto-populate manufacturer and model - leave empty for user to fill
						if (initialValues.hasManufacturerPlate === "no") {
							if (initialValues.manufacturer === 2719) {
								delete initialValues.manufacturer;
							}
							if (initialValues.modelNumber === "0") {
								delete initialValues.modelNumber;
							}
						}
					}

					// Set the data in both state and form
					setFormData(initialValues);
					form.setFieldsValue(initialValues);
					setFormConfig(configForNewWithCustomFields);

					// Fetch any data sources (standard form initialization)
					const allFs = getAllFields(
						configForNewWithCustomFields.config.sections,
					);
					const promises = allFs
						.filter(
							(f) =>
								f.dataSource?.type === "api" ||
								(f.options && Array.isArray(f.options)),
						)
						.map((f) => fetchFieldData(f, initialValues));
					await Promise.all(promises);
				},
				user?.organization?.forms,
			);

			// Re-enable features
			setInternalDataLoaded(true);
			setShouldAutoSave(user?.organization?.config?.auto_save_drafts ?? false);
			setIsCompleted(false);
			setPluginEnhancements({});
			setDraftId(undefined);
			setDraftDisabled(false);

			message.success("Started new form with preserved location.");
		} catch (err) {
			console.error("Submit & Start New error:", err);
			message.error("Failed to submit and start a new form.");
		} finally {
			dispatch({
				type: "SET_SUBMIT_STATE",
				payload: { isSubmittingNew: false },
			});
		}
	}

	/** Bottom navigation buttons */
	function renderNavigationButtons() {
		const { isSubmitting, isSubmittingNew } = state.submitState;

		const handleCancel = () => {
			// Cancel any pending auto-saves and clear state
			saveProgressDebounced.cancel();
			clearState();
			// Navigate back to the previous page or dashboard
			navigate(-1);
		};

		return (
			<div style={{ marginTop: "24px", textAlign: "center" }}>
				{/* Cancel button - always visible */}
				<Button
					style={{ margin: "0 8px" }}
					onClick={handleCancel}
					disabled={isSubmitting || isSubmittingNew}
				>
					Cancel
				</Button>

				{state.currentStep > 0 && (
					<Button
						style={{ margin: "0 8px" }}
						onClick={prev}
						disabled={isSubmitting || isSubmittingNew}
					>
						Previous
					</Button>
				)}
				{state.currentStep < stepsConfig.length - 1 && (
					<Button
						type="primary"
						onClick={next}
						disabled={isSubmitting || isSubmittingNew}
					>
						Next
					</Button>
				)}
				{state.currentStep === stepsConfig.length - 1 && (
					<>
						<Button
							type="primary"
							style={{ margin: "0 8px" }}
							loading={isSubmitting}
							disabled={isSubmittingNew}
							onClick={(e) => {
								e.preventDefault();
								onFinish("submit");
							}}
						>
							Submit
						</Button>
						<Button
							type="primary"
							loading={isSubmittingNew}
							disabled={isSubmitting}
							onClick={(e) => {
								e.preventDefault();
								onFinish("submitAndNew");
							}}
						>
							Submit & Enter New
						</Button>
					</>
				)}
			</div>
		);
	}

	const isMultipleSteps = stepsConfig.length > 10;

	/*-------------------------------------------------------------------
	 * Render
	 *-------------------------------------------------------------------*/
	return formConfig?.config?.sections ? (
		<Row className="px-2 w-100">
			<Form
				key={formKey}
				className="w-100"
				form={form}
				layout="vertical"
				onValuesChange={onFormValuesChange}
			>
				<div
					style={{ textAlign: "center", marginBottom: "12px" }}
					className="d-flex flex-column justify-content-center align-items-center gap-4"
				>
					{formLogo && (
						<img src={formLogo} alt="Form Logo" style={{ maxWidth: "200px" }} />
					)}
					<Title level={2}>{formName}</Title>
					{locationDisplayText && (
						<div
							style={{
								color: "#9333ea",
								fontSize: "16px",
								fontWeight: "500",
								marginTop: "-8px",
							}}
						>
							📍 {locationDisplayText}
						</div>
					)}
				</div>

				{!isMultipleSteps ? (
					<div className="steps-container d-flex flex-column align-items-center my-4 px-3">
						<Steps
							current={state.currentStep}
							size="default"
							className="steps-wrapper w-100"
							responsive
							direction="horizontal"
						>
							{stepsConfig.map((item, index) => (
								<Step
									key={index}
									className="step-item"
									onClick={async () => {
										if (
											index === state.currentStep ||
											(index > state.currentStep &&
												!(await validateCurrentStep()))
										) {
											return;
										}
										handleStepChange(index);
									}}
									icon={
										<Tooltip title={item.title}>
											<span
												className={`step-icon ${
													index === state.currentStep ? "active" : ""
												}`}
											>
												{index + 1}
											</span>
										</Tooltip>
									}
								/>
							))}
						</Steps>
						<div className="step-titles d-flex justify-content-between w-100 mt-2">
							{stepsConfig.map((_, idx) => (
								<div
									key={`${idx}-${stepsConfig[idx].title}`}
									className={`step-title text-center ${
										idx === state.currentStep ? "active" : ""
									}`}
									style={{
										width: `${100 / stepsConfig.length}%`,
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
									}}
								>
									<span>&nbsp;</span>
								</div>
							))}
						</div>
					</div>
				) : (
					<div className="progress-container d-flex flex-column align-items-center my-4 px-3">
						<Progress
							percent={Number.parseInt(
								(((state.currentStep + 1) / stepsConfig.length) * 100).toFixed(
									0,
								),
							)}
							status="active"
							showInfo
							className="progress-bar w-100"
							format={(percent) => `Step ${percent}% Completed`}
						/>
						<div className="step-select mt-3 d-flex align-items-center">
							<span>Navigate to: </span>
							<Select
								value={state.currentStep + 1}
								onChange={(val) => handleStepChange(val - 1)}
								style={{ marginLeft: "8px", minWidth: "120px" }}
								size="small"
							>
								{stepsConfig.map((st, idx) => (
									<Select.Option key={idx} value={idx + 1}>
										{`Step ${idx + 1}: ${st.title}`}
									</Select.Option>
								))}
							</Select>
						</div>
					</div>
				)}

				<div className="step-title text-center mt-4">
					<Title level={5}>{stepsConfig[state.currentStep]?.title}</Title>
				</div>

				{/* Render the step's content */}
				{stepsConfig[state.currentStep]?.content ? (
					<div style={{ marginTop: "24px" }}>
						{stepsConfig[state.currentStep].content}
					</div>
				) : (
					<Row className="px-2 w-100 d-flex flex-col justify-content-center align-content-center">
						<Loader />
					</Row>
				)}

				{/* Image preview modal */}
				<Modal
					open={previewVisible}
					title={previewTitle}
					footer={null}
					onCancel={() => setPreviewVisible(false)}
				>
					<Image
						src={previewImage}
						alt="Preview"
						wrapperClassName="w-100 cursor-pointer d-flex justify-content-center align-items-center"
						preview={{
							onVisibleChange: (vis) => {
								if (vis) return setPreviewVisible(false);
								return setPreviewVisible(true);
							},
							destroyOnClose: true,
							closeIcon: (
								<span
									className="p-2 bg-white"
									style={{ borderRadius: token.borderRadius }}
								>
									<LuXCircle size="1.5rem" color={token.colorPrimary} />
								</span>
							),
							maskStyle: { backgroundColor: token.colorBgMask },
							toolbarRender(originalNode) {
								return (
									<div
										style={{
											color: token.colorPrimary,
											backgroundColor: token.colorWhite,
											borderRadius: token.borderRadius,
										}}
									>
										{originalNode}
									</div>
								);
							},
						}}
					/>
				</Modal>

				{/* Bottom nav buttons */}
				{renderNavigationButtons()}
			</Form>
		</Row>
	) : (
		<Row className="px-2 w-100 d-flex flex-col justify-content-center align-content-center">
			<Spin indicator={<Loader />} tip="Loading form..." />
		</Row>
	);
};
